import * as THREE from "three";
import { WallType, WallEdgeConnection, OutlineSegment, OutlineSegmentCollection, WallConnectionStart, WallConnectionEnd, WallConnectionMiddle, isDoorType, isSlidingDoorType, isDoubleDoorType, isWindowType, isVoidType } from "../types/floorplannerTypes";
import type { FloorplannerStore } from "../store/floorplannerStore";
import { renderStore } from "../store/renderStore";
import { calculateWallEdgePoint, getWallAngle, getWallAngleAtEndpoint, determineConnectionSide } from "./edgeConnectionHelpers";
import { getEndpointImaginaryIntersections } from "./imaginaryLineHelpers";
import { getConnectionPoint } from "./connectionGeometry";
import { calculateWallImaginaryLines, intersectImaginaryLines } from "./imaginaryLineHelpers";
import { jointDetector } from "./JointDetector";
import { getQuadraticBezierPoint, getQuadraticBezierTangent, arcLengthToT } from "./arcUtils";
import { getSymbolInsertGeometry } from "./symbolAttachmentGeometry";
import { getMultiJointEndpointTrimPoint } from "./multiJointEndpointTrim";

// Lazy import to avoid circular dependency with floorplannerStore
// During store initialization/rehydration, floorplannerStore may not be fully
// exported yet, so we defer access until it's actually needed at runtime.
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    // Access via globalThis to avoid circular import issues
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      // Fallback: try dynamic import (this should rarely happen)
      throw new Error('FloorplannerStore not yet initialized - this function was called too early during initialization');
    }
  }
  return _floorplannerStore;
};

// Lazy import to avoid circular dependency with editorStore
let editorStore: any = null;
let editorStorePromise: Promise<any> | null = null;
const getEditorStore = () => {
  if (!editorStore) {
    if (!editorStorePromise) {
      editorStorePromise = import("../store/editorStore").then((module) => {
        editorStore = module.editorStore;
        return editorStore;
      }).catch((e) => {
        console.warn('Failed to load editorStore:', e);
        return null;
      });
    }
    // For synchronous access, return null if not yet loaded
    // Callers should handle async loading if needed
    return editorStore;
  }
  return editorStore;
};

/**
 * Helper to check if walls are collinear
 * Collinear if angle difference is close to 0 (parallel) or PI (anti-parallel/straight)
 * Using a generous tolerance (approx 11 degrees) to catch near-collinear snaps
 */
const getWallDirectionAtConnection = (
  wall: WallType,
  endpoint?: "start" | "end",
): THREE.Vector2 => {
  if (endpoint && wall.lineForm === "arc" && wall.controlPoint) {
    const angle = getWallAngleAtEndpoint(wall, endpoint);
    return new THREE.Vector2(Math.cos(angle), Math.sin(angle));
  }

  const direction = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = direction.length();
  if (length < 1e-6) {
    return new THREE.Vector2(1, 0);
  }

  return direction.divideScalar(length);
};

/**
 * Returns the overall chord direction of a wall (from start to end).
 *
 * For arc walls this is the straight-line direction between the endpoints and
 * is therefore independent of the bulge/control point. Used for collinearity
 * detection where the local tangent of an arc can mislead: a perpendicular
 * L-joint with a steep bulge has a tangent nearly parallel to the connecting
 * wall even though the arc's overall geometry is clearly not collinear. If
 * that case were treated as collinear, the straight wall's outline would not
 * be trimmed at the intersection and would visibly extend past the arc.
 */
const getWallChordDirection = (wall: WallType): THREE.Vector2 => {
  const direction = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = direction.length();
  if (length < 1e-6) {
    return new THREE.Vector2(1, 0);
  }
  return direction.divideScalar(length);
};

const toVector2 = (point: THREE.Vector2 | { x: number; y: number }): THREE.Vector2 =>
  point instanceof THREE.Vector2 ? point.clone() : new THREE.Vector2(point.x, point.y);

const edgeConnectionTouchesEndpoint = (
  connection: WallEdgeConnection,
  wallId: string,
  endpoint: "start" | "end",
): boolean =>
  (connection.sourceWallId === wallId && connection.sourceEndpoint === endpoint) ||
  (connection.targetWallId === wallId && connection.targetEndpoint === endpoint);

const getEndpointEdgeConnections = (
  wall: WallType,
  endpoint: "start" | "end",
): WallEdgeConnection[] => {
  const connections: WallEdgeConnection[] = [];
  const seen = new Set<string>();

  const addConnection = (connection: WallEdgeConnection) => {
    if (!edgeConnectionTouchesEndpoint(connection, wall.id, endpoint)) {
      return;
    }

    const key = [
      connection.sourceWallId,
      connection.sourceEndpoint,
      connection.targetWallId,
      connection.targetEndpoint,
      connection.sourceSide ?? "",
      connection.targetSide ?? "",
    ].join(":");
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    connections.push(connection);
  };

  (wall.edgeConnections || []).forEach(addConnection);

  // Edge topology can be stored one-way. The joint detector scans every wall,
  // so endpoint caps need the same inbound awareness or they can close over a
  // valid multi-joint while the fill polygon is correct.
  if (!renderStore.isDragging) {
    getFloorplannerStore().wallsMap.forEach((otherWall, otherWallId) => {
      if (otherWallId === wall.id) {
        return;
      }
      (otherWall.edgeConnections || []).forEach(addConnection);
    });
  }

  return connections;
};

const areWallsCollinear = (
  w1: WallType,
  w2: WallType,
  // Endpoint arguments are kept for API compatibility but intentionally not
  // used: collinearity must be decided by the overall wall directions, not by
  // an arc's local tangent at the joint (see getWallChordDirection).
  _w1Endpoint?: "start" | "end",
  _w2Endpoint?: "start" | "end",
) => {
  const d1 = getWallChordDirection(w1);
  const d2 = getWallChordDirection(w2);
  const a1 = Math.atan2(d1.y, d1.x);
  const a2 = Math.atan2(d2.y, d2.x);
  let diff = Math.abs(a1 - a2);
  if (diff > Math.PI) diff = 2 * Math.PI - diff;
  return diff < 0.2 || Math.abs(diff - Math.PI) < 0.2;
};

const connectionPositionToEndpoint = (
  position: number,
): "start" | "end" | "middle" => {
  if (position === WallConnectionStart) {
    return "start";
  }
  if (position === WallConnectionEnd) {
    return "end";
  }
  return "middle";
};

/**
 * Returns true when `point` lies on (or inside) the footprint of `wall` along
 * its axis. Used to detect collinear overlap: the perpendicular distance must be
 * within the wall's half-width and the axial projection must fall between the
 * endpoints (inclusive, with a small tolerance for the tip-to-tip case where the
 * point coincides with an endpoint).
 */
const isPointCoveredByWall = (
  point: THREE.Vector2,
  wall: WallType,
): boolean => {
  const start = toVector2(wall.start);
  const end = toVector2(wall.end);
  const dir = new THREE.Vector2().subVectors(end, start);
  const length = dir.length();
  if (length < 1e-6) {
    return false;
  }
  dir.divideScalar(length);
  const perp = new THREE.Vector2(-dir.y, dir.x);
  const rel = new THREE.Vector2().subVectors(point, start);
  const along = rel.dot(dir);
  const off = Math.abs(rel.dot(perp));
  const halfWidth = (wall.wallWidth ?? getFloorplannerStore().wallWidth) / 2;
  const tolerance = 1e-3;
  return along >= -tolerance && along <= length + tolerance && off <= halfWidth + tolerance;
};

/**
 * Detects whether `wall`'s endpoint is closed over by a COLLINEAR partner wall
 * that connects into this wall via a one-way "middle" edge connection.
 *
 * Background: when two collinear walls meet tip-to-tip (or one is dragged a bit
 * into the other), the snap can register the connection as `source.end -> target
 * middle`. Middle connections are stored one-way (no reciprocal), and because the
 * recorded `targetEndpoint` is "middle" rather than "start"/"end", the target
 * wall never recognises its endpoint as connected. It therefore draws a full
 * endcap right at the junction, closing the fill across what should be a seamless
 * collinear continuation. This scans the inbound connections so the target wall
 * can suppress that spurious endcap, matching how the joint actually looks.
 */
const hasCollinearMiddleCoverAtEndpoint = (
  wall: WallType,
  endpoint: "start" | "end",
): boolean => {
  // Mirror the gating of the one-way edge-connection scan: skip the O(n²) sweep
  // during drag (progressive geometry tolerates the transient endcap; it is
  // corrected on drop).
  if (renderStore.isDragging) {
    return false;
  }

  const endpointPos = toVector2(endpoint === "start" ? wall.start : wall.end);
  let covered = false;

  getFloorplannerStore().wallsMap.forEach((otherWall, otherWallId) => {
    if (covered || otherWallId === wall.id) {
      return;
    }
    (otherWall.edgeConnections || []).forEach((conn) => {
      if (covered) {
        return;
      }
      // Only inbound "middle" connections targeting this wall are missed by the
      // endpoint-aware checks elsewhere; endpoint-to-endpoint connections are
      // already handled by getEndpointEdgeConnections.
      if (conn.targetWallId !== wall.id || conn.targetEndpoint !== "middle") {
        return;
      }
      if (conn.sourceEndpoint !== "start" && conn.sourceEndpoint !== "end") {
        return;
      }
      if (!areWallsCollinear(wall, otherWall)) {
        return;
      }
      // The partner only closes over this endpoint when its footprint actually
      // covers it (tip-to-tip or overlap). A collinear partner meeting the far
      // interior leaves this endpoint as a genuine free end.
      if (isPointCoveredByWall(endpointPos, otherWall)) {
        covered = true;
      }
    });
  });

  return covered;
};

const getArcEndpointTrimPair = (
  wall: WallType,
  endpoint: "start" | "end",
): { left: THREE.Vector2; right: THREE.Vector2 } | null => {
  if (jointDetector.isEndpointInMultiJoint(wall.id, endpoint)) {
    return null;
  }

  const referencePoint = endpoint === "start"
    ? ((wall.end as THREE.Vector2).clone ? (wall.end as THREE.Vector2).clone() : new THREE.Vector2(wall.end.x, wall.end.y))
    : ((wall.start as THREE.Vector2).clone ? (wall.start as THREE.Vector2).clone() : new THREE.Vector2(wall.start.x, wall.start.y));
  const trimCandidates: Array<{
    left: THREE.Vector2;
    right: THREE.Vector2;
    score: number;
  }> = [];
  const seen = new Set<string>();

  const addTrimCandidate = (
    otherWallId: string,
    otherEndpoint: "start" | "end" | "middle",
  ) => {
    const dedupeKey = `${otherWallId}:${otherEndpoint}`;
    if (seen.has(dedupeKey)) {
      return;
    }
    seen.add(dedupeKey);

    const otherWall = getFloorplannerStore().wallsMap.get(otherWallId);
    if (!otherWall) {
      return;
    }

    const intersections = getEndpointImaginaryIntersections(
      wall,
      endpoint,
      otherWall,
      wall.wallWidth ?? getFloorplannerStore().wallWidth,
      {
        forceSharedEndpoint: otherEndpoint === "start" || otherEndpoint === "end",
      },
    );

    if (!intersections) {
      return;
    }

    trimCandidates.push({
      left: intersections.left.clone(),
      right: intersections.right.clone(),
      score:
        intersections.left.distanceToSquared(referencePoint) +
        intersections.right.distanceToSquared(referencePoint),
    });
  };

  (wall.edgeConnections || []).forEach((conn) => {
    const isSourceEndpoint = conn.sourceWallId === wall.id && conn.sourceEndpoint === endpoint;
    const isTargetEndpoint = conn.targetWallId === wall.id && conn.targetEndpoint === endpoint;

    if (!isSourceEndpoint && !isTargetEndpoint) {
      return;
    }

    addTrimCandidate(
      isSourceEndpoint ? conn.targetWallId : conn.sourceWallId,
      isSourceEndpoint ? conn.targetEndpoint : conn.sourceEndpoint,
    );
  });

  (wall.centerConnections || []).forEach((conn) => {
    const sourceEndpoint = connectionPositionToEndpoint(conn.sourcePosition);
    if (sourceEndpoint !== endpoint) {
      return;
    }

    addTrimCandidate(
      conn.id,
      connectionPositionToEndpoint(conn.targetPosition),
    );
  });

  if (trimCandidates.length === 0) {
    return null;
  }

  trimCandidates.sort((a, b) => a.score - b.score);
  return {
    left: trimCandidates[0].left,
    right: trimCandidates[0].right,
  };
};

/**
 * Representation of a portion of an arc outline polyline that should be cut
 * away so another wall can visually meet the curved wall at a T-junction.
 *
 * - `side`: which polyline of the arc is affected (`"left"` or `"right"`).
 * - `entry` / `exit`: the two points on that polyline where the connecting
 *   wall's edges intersect the arc's outline. They are the gap boundaries.
 * - `entryT` / `exitT`: arc-length parameters of the gap boundaries along the
 *   polyline, used to locate which polyline segment(s) to split.
 */
type ArcOutlineGap = {
  side: "left" | "right";
  entry: THREE.Vector2;
  exit: THREE.Vector2;
  entryT: number;
  exitT: number;
};

/**
 * Compute arc-length-normalised t-values for each vertex of a polyline.
 * Used as a 1-D coordinate along the polyline so we can order gap boundaries
 * and locate the polyline segment they land on.
 */
const computePolylineCumulativeLengths = (points: THREE.Vector2[]): number[] => {
  const cumulative: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    cumulative.push(cumulative[i - 1] + points[i - 1].distanceTo(points[i]));
  }
  return cumulative;
};

/**
 * Project a point onto a polyline and return both the closest point on the
 * polyline and its arc-length parameter. Returns `null` if the polyline has
 * no length. The search walks every segment, so for the 20-sample arc
 * polylines we use this is O(n) per call.
 */
const projectPointOntoPolyline = (
  point: THREE.Vector2,
  polyline: THREE.Vector2[],
): { point: THREE.Vector2; t: number; segmentIndex: number; distance: number } | null => {
  if (polyline.length < 2) {
    return null;
  }

  const cumulative = computePolylineCumulativeLengths(polyline);

  let bestSegment = 0;
  let bestProjection = polyline[0].clone();
  let bestDistance = Infinity;
  let bestT = 0;

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const ab = new THREE.Vector2().subVectors(b, a);
    const lengthSq = ab.lengthSq();
    if (lengthSq < 1e-12) continue;

    const ap = new THREE.Vector2().subVectors(point, a);
    let u = ap.dot(ab) / lengthSq;
    u = Math.max(0, Math.min(1, u));

    const projection = a.clone().addScaledVector(ab, u);
    const dist = projection.distanceTo(point);

    if (dist < bestDistance) {
      bestDistance = dist;
      bestSegment = i;
      bestProjection = projection;
      bestT = cumulative[i] + u * Math.sqrt(lengthSq);
    }
  }

  return {
    point: bestProjection,
    t: bestT,
    segmentIndex: bestSegment,
    distance: bestDistance,
  };
};

/**
 * Intersect a polyline with an infinite line defined by:
 *   dot((x - linePoint), lineNormal) = 0
 *
 * Returns the closest intersection to `linePoint` when multiple segments cross.
 */
const intersectPolylineWithInfiniteLine = (
  linePoint: THREE.Vector2,
  lineNormal: THREE.Vector2,
  polyline: THREE.Vector2[],
): { point: THREE.Vector2; t: number; segmentIndex: number; distance: number } | null => {
  if (polyline.length < 2) {
    return null;
  }

  const cumulative = computePolylineCumulativeLengths(polyline);
  const normal = lineNormal.clone();
  const normalLength = normal.length();
  if (normalLength < 1e-12) {
    return null;
  }
  normal.divideScalar(normalLength);

  let best: { point: THREE.Vector2; t: number; segmentIndex: number; distance: number } | null = null;
  const epsilon = 1e-8;

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];
    const ab = new THREE.Vector2().subVectors(b, a);
    const segLen = ab.length();
    if (segLen < epsilon) continue;

    const da = normal.dot(new THREE.Vector2().subVectors(a, linePoint));
    const db = normal.dot(new THREE.Vector2().subVectors(b, linePoint));

    let u: number | null = null;
    if (Math.abs(da) <= epsilon && Math.abs(db) <= epsilon) {
      // Segment lies nearly on the boundary line; choose the closest point on it.
      const lengthSq = ab.lengthSq();
      const ap = new THREE.Vector2().subVectors(linePoint, a);
      u = Math.max(0, Math.min(1, ap.dot(ab) / lengthSq));
    } else if (Math.abs(da) <= epsilon) {
      u = 0;
    } else if (Math.abs(db) <= epsilon) {
      u = 1;
    } else if (da * db < 0) {
      u = da / (da - db);
    }

    if (u === null) continue;
    if (u < -epsilon || u > 1 + epsilon) continue;

    const uClamped = Math.max(0, Math.min(1, u));
    const point = a.clone().addScaledVector(ab, uClamped);
    const t = cumulative[i] + segLen * uClamped;
    const distance = point.distanceTo(linePoint);

    if (!best || distance < best.distance) {
      best = { point, t, segmentIndex: i, distance };
    }
  }

  return best;
};

/**
 * Split a polyline into line segments while carving out one or more gaps
 * identified by arc-length ranges. Each gap contributes no segment; the
 * surrounding geometry keeps the original polyline vertices so the curve
 * stays smooth on both sides of the gap.
 *
 * Gaps with zero or negative span are treated as no-ops. Overlapping gaps are
 * merged by taking the union of their ranges.
 */
const cutPolylineAtGaps = (
  polyline: THREE.Vector2[],
  gaps: ArcOutlineGap[],
): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> => {
  if (polyline.length < 2) {
    return [];
  }

  const cumulative = computePolylineCumulativeLengths(polyline);
  const totalLength = cumulative[cumulative.length - 1];
  if (totalLength <= 0) {
    return [];
  }

  // Normalise each gap to ordered, clamped arc-length ranges along with the
  // actual polyline points where the gap begins and ends.
  const ranges: Array<{ startT: number; endT: number; startPoint: THREE.Vector2; endPoint: THREE.Vector2 }> = [];
  gaps.forEach(g => {
    const startT = Math.max(0, Math.min(totalLength, Math.min(g.entryT, g.exitT)));
    const endT = Math.max(0, Math.min(totalLength, Math.max(g.entryT, g.exitT)));
    if (endT - startT < 1e-6) {
      return;
    }
    const startPoint = g.entryT <= g.exitT ? g.entry.clone() : g.exit.clone();
    const endPoint = g.entryT <= g.exitT ? g.exit.clone() : g.entry.clone();
    ranges.push({ startT, endT, startPoint, endPoint });
  });

  ranges.sort((a, b) => a.startT - b.startT);

  // Merge overlapping ranges so we don't emit zero-length segments from a
  // secondary gap that happens to land inside a primary gap.
  const merged: typeof ranges = [];
  ranges.forEach(range => {
    const last = merged[merged.length - 1];
    if (last && range.startT <= last.endT) {
      if (range.endT > last.endT) {
        last.endT = range.endT;
        last.endPoint = range.endPoint;
      }
    } else {
      merged.push({ ...range });
    }
  });

  if (merged.length === 0) {
    const segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];
    for (let i = 0; i < polyline.length - 1; i++) {
      segments.push({ start: polyline[i].clone(), end: polyline[i + 1].clone() });
    }
    return segments;
  }

  /**
   * Emit polyline segments from arc-length `fromT` to `toT`, preserving the
   * intermediate vertices so the curve stays smooth. The boundary points
   * (`fromT`/`toT`) are inserted exactly at the gap edges.
   */
  const emitSegmentsForRange = (
    fromT: number,
    toT: number,
    fromPoint: THREE.Vector2,
    toPoint: THREE.Vector2,
    output: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>,
  ) => {
    if (toT - fromT < 1e-6) {
      return;
    }

    let current = fromPoint.clone();
    let currentT = fromT;

    for (let i = 1; i < polyline.length; i++) {
      const vertexT = cumulative[i];
      if (vertexT <= currentT + 1e-6) {
        continue;
      }
      if (vertexT >= toT - 1e-6) {
        break;
      }
      const vertex = polyline[i].clone();
      if (vertex.distanceTo(current) > 1e-6) {
        output.push({ start: current, end: vertex });
        current = vertex;
        currentT = vertexT;
      }
    }

    if (toPoint.distanceTo(current) > 1e-6) {
      output.push({ start: current, end: toPoint.clone() });
    }
  };

  const result: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];
  let cursorT = 0;
  let cursorPoint = polyline[0].clone();

  merged.forEach(gap => {
    emitSegmentsForRange(cursorT, gap.startT, cursorPoint, gap.startPoint, result);
    cursorT = gap.endT;
    cursorPoint = gap.endPoint.clone();
  });

  emitSegmentsForRange(cursorT, totalLength, cursorPoint, polyline[polyline.length - 1].clone(), result);

  return result;
};

/**
 * Determine whether a candidate pair of points clearly lies on one of the
 * two outline polylines. Projects both points onto each polyline and picks
 * the side with the smaller combined distance, rejecting pairs that don't
 * land close to either polyline (returns `null`).
 */
const chooseGapSideForArcOutline = (
  entry: THREE.Vector2,
  exit: THREE.Vector2,
  leftOutline: THREE.Vector2[],
  rightOutline: THREE.Vector2[],
  halfWidth: number,
): {
  side: "left" | "right";
  entry: THREE.Vector2;
  exit: THREE.Vector2;
  entryT: number;
  exitT: number;
} | null => {
  const leftEntry = projectPointOntoPolyline(entry, leftOutline);
  const leftExit = projectPointOntoPolyline(exit, leftOutline);
  const rightEntry = projectPointOntoPolyline(entry, rightOutline);
  const rightExit = projectPointOntoPolyline(exit, rightOutline);

  if (!leftEntry || !leftExit || !rightEntry || !rightExit) {
    return null;
  }

  const leftScore = leftEntry.distance + leftExit.distance;
  const rightScore = rightEntry.distance + rightExit.distance;
  // Reject candidates that aren't clearly closer to one side. Points from a
  // valid T-junction always land within roughly a wall width of the target
  // outline; anything farther than `halfWidth` typically means the
  // intersection is an artefact and the source wall doesn't really cut
  // through the arc on this side.
  const rejectionThreshold = Math.max(halfWidth * 2, 0.02);
  if (Math.min(leftScore, rightScore) > rejectionThreshold) {
    return null;
  }

  if (leftScore <= rightScore) {
    return {
      side: "left",
      entry: leftEntry.point,
      exit: leftExit.point,
      entryT: leftEntry.t,
      exitT: leftExit.t,
    };
  }

  return {
    side: "right",
    entry: rightEntry.point,
    exit: rightExit.point,
    entryT: rightEntry.t,
    exitT: rightExit.t,
  };
};

/**
 * Enumerate T-junction connections where another wall's endpoint lands on
 * this arc wall's middle and return the gap boundaries on the arc's outline
 * polylines. Scans both the arc's own `edgeConnections` (for completeness)
 * and every other wall's `edgeConnections` (since T-junction connections to
 * a middle are stored one-way on the source wall only).
 */
const collectArcMiddleConnectionGaps = (
  arcWall: WallType,
  leftOutlinePoints: THREE.Vector2[],
  rightOutlinePoints: THREE.Vector2[],
): ArcOutlineGap[] => {
  const gaps: ArcOutlineGap[] = [];
  const arcWidth = arcWall.wallWidth ?? getFloorplannerStore().wallWidth;
  const halfWidth = arcWidth / 2;
  const seen = new Set<string>();

  const pushGap = (sourceWall: WallType, sourceEndpoint: "start" | "end") => {
    const dedupeKey = `${sourceWall.id}:${sourceEndpoint}`;
    if (seen.has(dedupeKey)) {
      return;
    }
    seen.add(dedupeKey);

    const intersections = getEndpointImaginaryIntersections(
      sourceWall,
      sourceEndpoint,
      arcWall,
      sourceWall.wallWidth ?? getFloorplannerStore().wallWidth,
    );
    if (!intersections) {
      return;
    }

    const gap = chooseGapSideForArcOutline(
      intersections.left,
      intersections.right,
      leftOutlinePoints,
      rightOutlinePoints,
      halfWidth,
    );
    if (gap) {
      gaps.push(gap);
    }
  };

  // Case 1: the arc's own edgeConnections carry the T-junction (rare because
  // reciprocals aren't created for middle targets, but we still check for
  // completeness / forward-compatibility).
  (arcWall.edgeConnections || []).forEach(conn => {
    if (conn.targetWallId !== arcWall.id) {
      return;
    }
    if (conn.targetEndpoint !== "middle") {
      return;
    }
    const sourceWall = getFloorplannerStore().wallsMap.get(conn.sourceWallId);
    if (!sourceWall) {
      return;
    }
    pushGap(sourceWall, conn.sourceEndpoint);
  });

  // Case 2: another wall's edgeConnections target this arc's middle. This is
  // the usual storage pattern for T-junctions to an arc (source endpoint ->
  // arc middle is stored only on the source wall).
  getFloorplannerStore().wallsMap.forEach(otherWall => {
    if (otherWall.id === arcWall.id) {
      return;
    }
    (otherWall.edgeConnections || []).forEach(conn => {
      if (conn.targetWallId !== arcWall.id || conn.targetEndpoint !== "middle") {
        return;
      }
      if (conn.sourceEndpoint !== "start" && conn.sourceEndpoint !== "end") {
        return;
      }
      pushGap(otherWall, conn.sourceEndpoint);
    });
  });

  return gaps;
};

/**
 * Creates ArcOutlineGap items for each symbol attachment on an arc wall.
 * Each gap spans from the outline point at tA to the outline point at tB
 * (the Bézier parameters of the symbol's straight-insert boundaries).
 * This correctly cuts the curved outline at the opening without needing
 * to know the outline's absolute arc length.
 */
const collectArcSymbolAttachmentGaps = (
  arcWall: WallType,
  leftOutlinePoints: THREE.Vector2[],
  rightOutlinePoints: THREE.Vector2[],
): ArcOutlineGap[] => {
  const gaps: ArcOutlineGap[] = [];
  if (!arcWall.symbolAttachments?.length) return gaps;

  const store = getFloorplannerStore();

  for (const attachment of arcWall.symbolAttachments) {
    const symbol = store.symbolsMap.get(attachment.symbolId);
    if (!symbol) continue;

    const insertGeom = getSymbolInsertGeometry(arcWall, symbol, attachment.positionFromStart, store.doorFrameWidth);
    const { tA, tB, pA, pB, tangent } = insertGeom;
    if (tB - tA < 1e-5) continue;

    // The visible arc outline must be cut where the straight insert's endcap
    // lines intersect the offset polylines. Those endcaps are perpendicular to
    // the insert chord, not to the local curve tangent at tA/tB.
    const fallbackInterp = (points: THREE.Vector2[], t: number): THREE.Vector2 => {
      const nSegments = points.length - 1;
      const idx = Math.max(0, Math.min(nSegments, t * nSegments));
      const lo = Math.floor(idx);
      const hi = Math.min(lo + 1, nSegments);
      const frac = idx - lo;
      return new THREE.Vector2(
        points[lo].x + (points[hi].x - points[lo].x) * frac,
        points[lo].y + (points[hi].y - points[lo].y) * frac,
      );
    };
    const fallbackProject = (
      polyline: THREE.Vector2[],
      point: THREE.Vector2,
    ) => projectPointOntoPolyline(point, polyline);

    const leftEntryProj =
      intersectPolylineWithInfiniteLine(pA, tangent, leftOutlinePoints) ??
      fallbackProject(leftOutlinePoints, fallbackInterp(leftOutlinePoints, tA));
    const leftExitProj =
      intersectPolylineWithInfiniteLine(pB, tangent, leftOutlinePoints) ??
      fallbackProject(leftOutlinePoints, fallbackInterp(leftOutlinePoints, tB));
    const rightEntryProj =
      intersectPolylineWithInfiniteLine(pA, tangent, rightOutlinePoints) ??
      fallbackProject(rightOutlinePoints, fallbackInterp(rightOutlinePoints, tA));
    const rightExitProj =
      intersectPolylineWithInfiniteLine(pB, tangent, rightOutlinePoints) ??
      fallbackProject(rightOutlinePoints, fallbackInterp(rightOutlinePoints, tB));

    if (leftEntryProj && leftExitProj) {
      gaps.push({
        side: "left",
        entry: leftEntryProj.point,
        exit: leftExitProj.point,
        entryT: leftEntryProj.t,
        exitT: leftExitProj.t,
      });
    }
    if (rightEntryProj && rightExitProj) {
      gaps.push({
        side: "right",
        entry: rightEntryProj.point,
        exit: rightExitProj.point,
        entryT: rightEntryProj.t,
        exitT: rightExitProj.t,
      });
    }
  }

  return gaps;
};

/**
 * Outline Segmentation - Segmented Line Rendering for Edge-Based Connections
 *
 * PURPOSE:
 * This file creates segmented wall outlines with pre-calculated gaps at connection points.
 * Instead of using filled rectangles with clipping, we split outlines into multiple line segments.
 *
 * WHY SEGMENTATION:
 * THREE.js LineBasicMaterial cannot use clipping planes, so we pre-calculate gaps into the geometry.
 * This approach is simpler, faster, and more flexible than the legacy clipping system.
 *
 * THREE.JS LIMITATION:
 * - LineBasicMaterial: Cannot use clipping planes
 * - Line width: Limited to 1 pixel in most browsers
 * - Solution: Use LineSegments2 from three-stdlib for thick lines with segmented geometry
 *
 * ALGORITHM:
 * 1. Calculate left/right outline positions
 * 2. Split at edge connections (0.02 unit gap)
 * 3. Split at mid-point connections (proportional gap)
 * 4. Split at cutouts (door/window openings)
 * 5. Merge overlapping segments
 *
 * GAP TYPES:
 * - Edge connections: Fixed 0.02 units
 * - Mid-point connections: Connected wall width / 4
 * - Cutouts: Full opening (no additional gap)
 *
 * INTEGRATION:
 * - Called from renderStore.generateOutlineSegments()
 * - Output stored in renderStore.outlineSegmentsMap
 * - Rendered as LineSegments2 at z=0.001
 * - Works with solid, dashed, and future dotted lines
 *
 * RELATED FILES:
 * - WallOutlineSegment.tsx: Renders individual segments
 * - WallOutlineStructure.tsx: Container component
 * - OUTLINE_SEGMENTATION.md: Complete documentation
 *
 * @see OUTLINE_SEGMENTATION.md - Detailed documentation
 */

/**
 * Generate outline segments with gaps for a wall based on edge connections.
 *
 * This utility calculates line segments for wall outlines (left/right) with gaps
 * at connection points (edge connections, mid-point connections, and cutouts).
 * The segments are pre-calculated and stored to avoid repeated computation during rendering.
 *
 * @param wall - The wall to generate outline segments for
 * @returns OutlineSegmentCollection containing all segments for the wall
 */
export function createOutlineSegments(wall: WallType): OutlineSegmentCollection {
  // console.group(`📐 createOutlineSegments - Wall ${wall.id}`);
  // console.log(`  Wall start: (${wall.start.x.toFixed(3)}, ${wall.start.y.toFixed(3)})`);
  // console.log(`  Wall end: (${wall.end.x.toFixed(3)}, ${wall.end.y.toFixed(3)})`);
  // console.log(`  Wall width: ${wall.wallWidth || getFloorplannerStore().wallWidth}`);
  // console.log(`  Line weight: ${wall.lineWeight || getFloorplannerStore().wallLineWeight}`);
  // const edgeConnections = wall.edgeConnections || [];
  // const regularConnections = wall.centerConnections || [];
  // console.log(`  Edge connections: ${edgeConnections.length}`);
  // console.log(`  Regular connections: ${regularConnections.length}`);

  // Outline segmentation is always active

  // STEP 1: Calculate wall direction and perpendicular vectors
  // Wall direction: from start to end (unit vector)
  const wallDirection = new THREE.Vector2()
    .subVectors(wall.end, wall.start)
    .normalize();
  // Perpendicular: rotate 90° counterclockwise for left side
  const wallPerpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

  // STEP 2: Get wall properties
  // Use wall-specific values or fall back to store defaults
  const wallWidth = wall.wallWidth || getFloorplannerStore().wallWidth;
  const lineWeight = wall.lineWeight || getFloorplannerStore().wallLineWeight;
  const lineColor = wall.lineColor || 0x000000;
  const dashed = wall.lineType === "dashed";

  // STEP 3: Calculate outline positions
  // Outlines should be at the exact edge of the fill (wallWidth/2 from center)
  // The line thickness is handled visually by LineMaterial, not by geometry offset
  const leftOffset = wallWidth / 2;
  const rightOffset = -wallWidth / 2;

  // For arc walls, generate polyline outlines that follow the curve
  const isArc = wall.lineForm === "arc" && wall.controlPoint;
  const ARC_OUTLINE_SEGMENTS = 20; // Number of segments to approximate the arc

  // Calculate start and end points for both outlines (used for endcaps and as defaults)
  const leftStart = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, leftOffset);
  const leftEnd = new THREE.Vector2().copy(wall.end).addScaledVector(wallPerpendicular, leftOffset);
  const rightStart = new THREE.Vector2().copy(wall.start).addScaledVector(wallPerpendicular, rightOffset);
  const rightEnd = new THREE.Vector2().copy(wall.end).addScaledVector(wallPerpendicular, rightOffset);

  // For arc walls, build polyline outline points along the curve
  let leftOutlinePoints: THREE.Vector2[];
  let rightOutlinePoints: THREE.Vector2[];

  if (isArc && wall.controlPoint) {
    leftOutlinePoints = [];
    rightOutlinePoints = [];
    for (let i = 0; i <= ARC_OUTLINE_SEGMENTS; i++) {
      const t = i / ARC_OUTLINE_SEGMENTS;
      const pointOnCurve = getQuadraticBezierPoint(t, wall.start, wall.controlPoint, wall.end);
      // Use tangent-based perpendicular for proper curve offset
      const tangent = getQuadraticBezierTangent(t, wall.start, wall.controlPoint, wall.end);
      const tangentLength = tangent.length();
      if (tangentLength > 1e-6) {
        tangent.normalize();
        const perp = new THREE.Vector2(-tangent.y, tangent.x);
        leftOutlinePoints.push(pointOnCurve.clone().addScaledVector(perp, leftOffset));
        rightOutlinePoints.push(pointOnCurve.clone().addScaledVector(perp, rightOffset));
      } else {
        leftOutlinePoints.push(pointOnCurve.clone().addScaledVector(wallPerpendicular, leftOffset));
        rightOutlinePoints.push(pointOnCurve.clone().addScaledVector(wallPerpendicular, rightOffset));
      }
    }

    const startTrimPair = getArcEndpointTrimPair(wall, "start");
    if (startTrimPair) {
      leftOutlinePoints[0] = startTrimPair.left.clone();
      rightOutlinePoints[0] = startTrimPair.right.clone();
    }

    const endTrimPair = getArcEndpointTrimPair(wall, "end");
    if (endTrimPair) {
      leftOutlinePoints[leftOutlinePoints.length - 1] = endTrimPair.left.clone();
      rightOutlinePoints[rightOutlinePoints.length - 1] = endTrimPair.right.clone();
    }
  } else {
    leftOutlinePoints = [leftStart, leftEnd];
    rightOutlinePoints = [rightStart, rightEnd];
  }

  // console.log(`  Left outline: start=(${leftStart.x.toFixed(3)}, ${leftStart.y.toFixed(3)}), end=(${leftEnd.x.toFixed(3)}, ${leftEnd.y.toFixed(3)})`);
  // console.log(`  Right outline: start=(${rightStart.x.toFixed(3)}, ${rightStart.y.toFixed(3)}), end=(${rightEnd.x.toFixed(3)}, ${rightEnd.y.toFixed(3)})`);
  // console.log(`  Wall direction: (${wallDirection.x.toFixed(3)}, ${wallDirection.y.toFixed(3)})`);
  // console.log(`  Wall perpendicular: (${wallPerpendicular.x.toFixed(3)}, ${wallPerpendicular.y.toFixed(3)})`);
  // console.log(`  Offset: left=${leftOffset.toFixed(3)}, right=${rightOffset.toFixed(3)}`);

  // STEP 4: Split lines at different gap sources
  // This is where we create segments with gaps
  // For arc walls, we work with polyline segments instead of straight lines
  let leftSegments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>;
  let rightSegments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>;

  if (isArc && leftOutlinePoints.length > 2) {
    // Arc wall: render the sampled polyline directly.
    // Endpoint trimming and partial endcap bridging for straight walls creates
    // visibly straight connector artifacts on curved walls.
    const polylineToSegments = (points: THREE.Vector2[]) => {
      const segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];
      for (let i = 0; i < points.length - 1; i++) {
        segments.push({ start: points[i].clone(), end: points[i + 1].clone() });
      }
      return segments;
    };

    // Collect T-junction gaps where another wall's endpoint lands on this arc's
    // middle. Without this the arc's outline stays closed across the connection
    // site (the straight wall visually runs INTO the arc instead of meeting it
    // at an opening) because the generic `splitLineAtEdgeConnections` path is
    // skipped for arcs to avoid chord-based artifacts.
    const arcGaps = [
      ...collectArcMiddleConnectionGaps(wall, leftOutlinePoints, rightOutlinePoints),
      ...collectArcSymbolAttachmentGaps(wall, leftOutlinePoints, rightOutlinePoints),
    ];

    const leftGapSegments = arcGaps.filter(g => g.side === "left");
    const rightGapSegments = arcGaps.filter(g => g.side === "right");

    leftSegments = leftGapSegments.length > 0
      ? cutPolylineAtGaps(leftOutlinePoints, leftGapSegments)
      : polylineToSegments(leftOutlinePoints);
    rightSegments = rightGapSegments.length > 0
      ? cutPolylineAtGaps(rightOutlinePoints, rightGapSegments)
      : polylineToSegments(rightOutlinePoints);
  } else {
    leftSegments = splitLineAtEdgeConnections(leftStart, leftEnd, wall, "left");
    rightSegments = splitLineAtEdgeConnections(rightStart, rightEnd, wall, "right");
  }

  // Split lines at mid-point connections (preserve existing behavior)
  const leftWithMid = isArc ? leftSegments : splitLineAtMidConnections(leftStart, leftEnd, wall, leftSegments, "left");
  const rightWithMid = isArc ? rightSegments : splitLineAtMidConnections(rightStart, rightEnd, wall, rightSegments, "right");

  // Split lines at cutouts (doors, windows, voids) - for cutouts defined in wall.cutouts
  const leftWithCutouts = isArc ? leftWithMid : splitLineAtCutouts(wallDirection, leftStart, leftEnd, wall, "left", leftWithMid);
  const rightWithCutouts = isArc ? rightWithMid : splitLineAtCutouts(wallDirection, rightStart, rightEnd, wall, "right", rightWithMid);

  // Split lines at symbol attachments (doors, windows, voids that are attached to the wall)
  // This is separate from cutouts to avoid affecting wall fill shapes.
  // Arc walls handle symbol attachment gaps earlier via collectArcSymbolAttachmentGaps,
  // so we skip the straight-wall chord-projection logic here for arc walls.
  const leftWithSymbols = isArc ? leftWithCutouts : splitLineAtSymbolAttachments(wall, "left", leftWithCutouts);
  const rightWithSymbols = isArc ? rightWithCutouts : splitLineAtSymbolAttachments(wall, "right", rightWithCutouts);

  //console.log(`  Before merging: left=${leftWithSymbols.length}, right=${rightWithSymbols.length}`);
  // Merge overlapping segments
  const filterDegenerateSegments = (
    segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>
  ): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> => {
    const epsilon = 1e-4;
    return segments
      .map((segment) => ({
        start: segment.start.clone(),
        end: segment.end.clone(),
      }))
      .filter((segment) => segment.start.distanceTo(segment.end) >= epsilon);
  };

  // Arc outlines are already emitted as sequential polyline segments.
  // Merging adjacent segments would flatten the curve into a chord.
  const finalLeftSegments = isArc
    ? filterDegenerateSegments(leftWithSymbols)
    : mergeOverlappingSegments(leftWithSymbols, 0.001, leftStart, wallDirection);
  const finalRightSegments = isArc
    ? filterDegenerateSegments(rightWithSymbols)
    : mergeOverlappingSegments(rightWithSymbols, 0.001, rightStart, wallDirection);
  //console.log(`  After merging: left=${finalLeftSegments.length}, right=${finalRightSegments.length}`);

  // Create OutlineSegment objects
  // NOTE: Using original lineWeight (not converted) for thickness because LineMaterial
  // linewidth expects UI units, not world units. The convertLineWeightToWorld function
  // is only for geometry calculations (offsets), not for visual thickness.
  const leftOutlineSegments: OutlineSegment[] = finalLeftSegments.map((segment, index) => ({
    id: `${wall.id}-left-${index}`,
    wallId: wall.id,
    side: "left",
    start: segment.start,
    end: segment.end,
    thickness: lineWeight, // Use original lineWeight for visual thickness
    color: lineColor,
    dashed,
  }));

  const rightOutlineSegments: OutlineSegment[] = finalRightSegments.map((segment, index) => ({
    id: `${wall.id}-right-${index}`,
    wallId: wall.id,
    side: "right",
    start: segment.start,
    end: segment.end,
    thickness: lineWeight, // Use original lineWeight for visual thickness
    color: lineColor,
    dashed,
  }));

  // Endcap segments (not affected by edge connections)
  // NOTE: Endcaps are always solid (not dashed) to match legacy behavior
  // Suppress endcaps at edge-connected endpoints
  const endCapSegments: OutlineSegment[] = [];

  /**
   * Helper function to check if a wall endpoint is edge-connected.
   * Returns true if:
   * - wall.edgeConnections contains an entry with sourceWallId===wall.id && sourceEndpoint===endpoint
   *   or targetWallId===wall.id && targetEndpoint===endpoint, OR
   * - wall.centerConnections contains a connection where sourcePosition equals the endpoint
   *   (using WallConnectionStart/WallConnectionEnd) and the connected wall exists (T-junction).
   */
  const hasEdgeConnectedEndpoint = (wall: WallType, endpoint: 'start' | 'end'): boolean => {
    // Check edge connections
    const hasEdgeConnection = getEndpointEdgeConnections(wall, endpoint).length > 0;

    if (hasEdgeConnection) {
      return true;
    }

    // Check regular connections for T-junctions
    const endpointPosition = endpoint === 'start' ? WallConnectionStart : WallConnectionEnd;
    const hasRegularConnection = (wall.centerConnections || []).some(conn => {
      if (conn.sourcePosition === endpointPosition) {
        // Verify the connected wall exists (T-junction)
        const connectedWall = getFloorplannerStore().findObjectId(conn.id);
        return connectedWall !== undefined;
      }
      return false;
    });

    return hasRegularConnection;
  };

  /**
   * Helper function to check if a wall endpoint is part of a multi-joint (3+ walls).
   * When true, the multi-joint system handles the joint polygon and outlines,
   * so we should suppress all endcaps and partial endcaps at this endpoint.
   */
  const isEndpointInMultiJoint = (wall: WallType, endpoint: 'start' | 'end'): boolean => {
    return jointDetector.isEndpointInMultiJoint(wall.id, endpoint);
  };

  // Check if start endpoint has edge connection (including T-junctions)
  const hasStartConnection = hasEdgeConnectedEndpoint(wall, 'start');
  const startIsMultiJoint = isEndpointInMultiJoint(wall, 'start');

  // Check if end endpoint has edge connection (including T-junctions)
  const hasEndConnection = hasEdgeConnectedEndpoint(wall, 'end');
  const endIsMultiJoint = isEndpointInMultiJoint(wall, 'end');

  // A collinear partner connected via a one-way "middle" edge connection closes
  // over this wall's endpoint (tip-to-tip or slight overlap). Such joints would
  // otherwise draw a full endcap across the seam because the inbound connection
  // records "middle" rather than this wall's actual endpoint. Suppress the endcap
  // so the collinear fills read as one continuous wall.
  const startCollinearCovered = !startIsMultiJoint && !isArc && hasCollinearMiddleCoverAtEndpoint(wall, 'start');
  const endCollinearCovered = !endIsMultiJoint && !isArc && hasCollinearMiddleCoverAtEndpoint(wall, 'end');

  // For arc walls, use the tangent-based polyline endpoints for endcaps
  const arcLeftStart = isArc ? leftOutlinePoints[0] : leftStart;
  const arcRightStart = isArc ? rightOutlinePoints[0] : rightStart;
  const arcLeftEnd = isArc ? leftOutlinePoints[leftOutlinePoints.length - 1] : leftEnd;
  const arcRightEnd = isArc ? rightOutlinePoints[rightOutlinePoints.length - 1] : rightEnd;

  // Only add start endcap if not edge-connected
  if (startCollinearCovered) {
    // Collinear partner covers this endpoint - the wall continues through the
    // junction, so no endcap (full or partial) should close the fill here.
  } else if (!hasStartConnection) {
    endCapSegments.push({
      id: `${wall.id}-endcap-start`,
      wallId: wall.id,
      side: "left", // endcaps don't have sides, using "left" as default
      start: arcLeftStart,
      end: arcRightStart,
      thickness: lineWeight, // Use original lineWeight for visual thickness
      color: lineColor,
      dashed: false, // Endcaps are always solid
    });
  } else if (!startIsMultiJoint && !isArc) {
    // If connected but NOT a multi-joint, check for partial endcaps due to width mismatches
    // For multi-joints, the joint polygon handles everything - no partial endcaps needed
    const partials = generatePartialEndCaps(wall, 'start', arcLeftStart, arcRightStart, lineWeight, lineColor);
    endCapSegments.push(...partials);
  }
  // If startIsMultiJoint, suppress all endcaps - joint polygon handles it

  // Only add end endcap if not edge-connected
  if (endCollinearCovered) {
    // Collinear partner covers this endpoint - the wall continues through the
    // junction, so no endcap (full or partial) should close the fill here.
  } else if (!hasEndConnection) {
    endCapSegments.push({
      id: `${wall.id}-endcap-end`,
      wallId: wall.id,
      side: "left", // endcaps don't have sides, using "left" as default
      start: arcRightEnd,
      end: arcLeftEnd,
      thickness: lineWeight, // Use original lineWeight for visual thickness
      color: lineColor,
      dashed: false, // Endcaps are always solid
    });
  } else if (!endIsMultiJoint && !isArc) {
    // If connected but NOT a multi-joint, check for partial endcaps due to width mismatches
    // For multi-joints, the joint polygon handles everything - no partial endcaps needed
    const partials = generatePartialEndCaps(wall, 'end', arcRightEnd, arcLeftEnd, lineWeight, lineColor);
    endCapSegments.push(...partials);
  }
  // If endIsMultiJoint, suppress all endcaps - joint polygon handles it

  //console.log(`  Endcap segments: start=${!hasStartConnection ? 'added' : 'suppressed'}, end=${!hasEndConnection ? 'added' : 'suppressed'}`);
  //console.log(`  Final segment counts: left=${leftOutlineSegments.length}, right=${rightOutlineSegments.length}, endcaps=${endCapSegments.length}`);
  // console.groupEnd();

  return {
    wallId: wall.id,
    leftSegments: leftOutlineSegments,
    rightSegments: rightOutlineSegments,
    endCapSegments,
    geometryVersion: wall.geometryVersion || 0,
  };
}

/**
 * Split a line at edge connection points to create gaps.
 *
 * @param lineStart - Start point of the line
 * @param lineEnd - End point of the line
 * @param wall - The wall this line belongs to
 * @param side - Which side of the wall ("left" or "right")
 * @returns Array of segment start/end pairs
 */
export function splitLineAtEdgeConnections(
  lineStart: THREE.Vector2,
  lineEnd: THREE.Vector2,
  wall: WallType,
  side: "left" | "right"
): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> {
  // Always split lines at edge connections

  // Get edge connections for this wall
  const edgeConnections = wall.edgeConnections || [];
  const originalLineStart = lineStart.clone();
  const originalLineEnd = lineEnd.clone();
  const clonePoint = (point: THREE.Vector2 | { x: number; y: number }) =>
    (point as THREE.Vector2).clone ? (point as THREE.Vector2).clone() : new THREE.Vector2(point.x, point.y);

  // Filter connections for this wall from its own array
  let wallConnections = edgeConnections.filter(conn =>
    conn.sourceWallId === wall.id || conn.targetWallId === wall.id
  );

  // PERFORMANCE OPTIMIZATION: Skip the expensive O(n²) iteration during drag operations.
  // During dragging, we rely on the wall's own edgeConnections which should be properly maintained.
  // The full scan is only needed for static rendering when connections might be asymmetric.
  const isDragging = renderStore.isDragging;

  if (!isDragging) {
    // CRITICAL FIX: For T-junction edge connections (targetEndpoint === "middle"),
    // the connection is only stored in the source wall's edgeConnections array.
    // We need to search ALL walls' edge connections to find connections targeting this wall.
    // NOTE: This is O(n²) but only runs during static rendering, not during drag.
    for (const otherWall of getFloorplannerStore().wallsMap.values()) {
      if (otherWall.id === wall.id) continue; // Skip self

      const otherConnections = otherWall.edgeConnections || [];
      for (const conn of otherConnections) {
        // If this connection targets the current wall and isn't already in wallConnections
        if (conn.targetWallId === wall.id) {
          const isDuplicate = wallConnections.some(existing =>
            existing.sourceWallId === conn.sourceWallId &&
            existing.targetWallId === conn.targetWallId &&
            existing.sourceEndpoint === conn.sourceEndpoint &&
            existing.targetEndpoint === conn.targetEndpoint
          );
          if (!isDuplicate) {
            wallConnections.push(conn);
          }
        }
      }
    }
  }

  // console.log(`    splitLineAtEdgeConnections (${side}): Found ${wallConnections.length} edge connection(s)`);

  // ALSO check regular connections for endpoint connections
  // When a wall endpoint connects to another wall's mid-point (T-junction),
  // the connection might only exist in the regular connections array
  const regularConnections = wall.centerConnections || [];
  const endpointConnections = regularConnections.filter(conn =>
    conn.sourcePosition === WallConnectionStart || conn.sourcePosition === WallConnectionEnd
  );
  // console.log(`    Also checking ${regularConnections.length} regular connection(s), found ${endpointConnections.length} endpoint connection(s)`);

  // For each endpoint connection in regular connections, create a pseudo edge connection
  // so outline segmentation can create gaps at these T-junction points
  for (const conn of endpointConnections) {
    // Calculate derived source endpoint for this connection
    const derivedSourceEndpoint = conn.sourcePosition === WallConnectionStart ? "start" : "end";

    // Enhanced duplicate detection: check both directions
    // Treat as duplicate if any existing edge connection satisfies:
    // - (ec.sourceWallId===wall.id && ec.targetWallId===conn.id && ec.sourceEndpoint===derivedSourceEndpoint) OR
    // - (ec.sourceWallId===conn.id && ec.targetWallId===wall.id && ec.targetEndpoint===derivedSourceEndpoint)
    // Also ensure pseudo target uses 'middle' and should not collide with true endpoint-to-endpoint edge connections
    const isDuplicate = edgeConnections.some(ec => {
      // Check forward direction: wall -> conn
      if (ec.sourceWallId === wall.id && ec.targetWallId === conn.id && ec.sourceEndpoint === derivedSourceEndpoint) {
        return true;
      }
      // Check reverse direction: conn -> wall
      if (ec.sourceWallId === conn.id && ec.targetWallId === wall.id && ec.targetEndpoint === derivedSourceEndpoint) {
        return true;
      }
      // Also check that we're not colliding with true endpoint-to-endpoint connections
      // (pseudo connections use 'middle' as target, so endpoint-to-endpoint would have different targetEndpoint)
      if ((ec.sourceWallId === wall.id && ec.targetWallId === conn.id) ||
        (ec.sourceWallId === conn.id && ec.targetWallId === wall.id)) {
        // If there's already an endpoint-to-endpoint connection, don't create pseudo connection
        // Note: sourceEndpoint can only be "start" | "end", so we only need to check targetEndpoint
        if (ec.targetEndpoint !== "middle") {
          return true;
        }
      }
      return false;
    });

    if (isDuplicate) {
      continue;
    }

    const connectedWall = getFloorplannerStore().findObjectId(conn.id) as WallType | undefined;
    if (!connectedWall) {
      continue;
    }

    // Skip pseudo connection creation for simple endpoint-to-endpoint L-joints
    // unless no explicit edge connection exists. In that case we synthesize a
    // minimal edge connection so outlines can trim correctly in center mode.
    const isEndpointToEndpointConnection =
      conn.targetPosition === WallConnectionStart || conn.targetPosition === WallConnectionEnd;
    if (isEndpointToEndpointConnection) {
      const targetEndpoint =
        conn.targetPosition === WallConnectionStart ? "start" : "end";

      const hasExistingConnection = wallConnections.some(existing =>
        (existing.sourceWallId === wall.id &&
          existing.targetWallId === conn.id &&
          existing.sourceEndpoint === derivedSourceEndpoint &&
          existing.targetEndpoint === targetEndpoint) ||
        (existing.sourceWallId === conn.id &&
          existing.targetWallId === wall.id &&
          existing.sourceEndpoint === targetEndpoint &&
          existing.targetEndpoint === derivedSourceEndpoint)
      );

      if (hasExistingConnection) {
        continue;
      }

      const connectedWallAngle = getWallAngle(connectedWall);
      const sourceEdgePointData = calculateWallEdgePoint(
        wall,
        derivedSourceEndpoint,
        connectedWallAngle
      );
      const sourceWallAngle = getWallAngle(wall);
      const targetEdgePointData = calculateWallEdgePoint(
        connectedWall,
        targetEndpoint,
        sourceWallAngle
      );

      wallConnections.push({
        sourceWallId: wall.id,
        targetWallId: conn.id,
        sourceEndpoint: derivedSourceEndpoint,
        targetEndpoint,
        sourceEdgePoint: sourceEdgePointData.edgePosition.clone(),
        targetEdgePoint: targetEdgePointData.edgePosition.clone(),
        gapDistance: 0,
        connectionAngle: Math.abs(connectedWallAngle - sourceWallAngle),
        id: `${wall.id}-${conn.id}-${derivedSourceEndpoint}-${targetEndpoint}`,
      } as any);
      continue;
    }

    // Calculate the edge point for this endpoint
    const endpoint = conn.sourcePosition === WallConnectionStart ? clonePoint(wall.start) : clonePoint(wall.end);
    const sourceEndpoint = conn.sourcePosition === WallConnectionStart ? "start" : "end";

    // Calculate proper edge points for both walls using calculateWallEdgePoint
    // For the source wall (endpoint connection): Use connected wall's angle
    const connectedWallAngle = getWallAngle(connectedWall);
    const sourceEdgePointData = calculateWallEdgePoint(wall, sourceEndpoint, connectedWallAngle);

    // CRITICAL FIX: For the target wall, we need to find where the source endpoint actually
    // intersects with the target wall, not just assume "middle"
    // Project the source endpoint onto the target wall's centerline
    const targetWallStart = clonePoint(connectedWall.start);
    const targetWallEnd = clonePoint(connectedWall.end);
    const targetWallDirection = new THREE.Vector2()
      .subVectors(targetWallEnd, targetWallStart)
      .normalize();
    const toSourceEndpoint = new THREE.Vector2().subVectors(endpoint, targetWallStart);
    const targetWallLength = targetWallStart.distanceTo(targetWallEnd);
    const projectionT = toSourceEndpoint.dot(targetWallDirection) / targetWallLength;
    const clampedProjectionT = Math.max(0, Math.min(1, projectionT));

    // Calculate the actual projection point on the target wall's centerline
    const projectionPoint = new THREE.Vector2()
      .copy(targetWallStart)
      .addScaledVector(new THREE.Vector2().subVectors(targetWallEnd, targetWallStart), clampedProjectionT);

    // Now calculate the edge point at this projection position
    // Use position-based side detection instead of angle-based
    const sourceWallCenter = new THREE.Vector2(
      (wall.start.x + wall.end.x) / 2,
      (wall.start.y + wall.end.y) / 2
    );
    const targetPerpendicular = new THREE.Vector2(-targetWallDirection.y, targetWallDirection.x);
    const toSourceWall = new THREE.Vector2().subVectors(sourceWallCenter, projectionPoint);
    const dotProduct = toSourceWall.dot(targetPerpendicular);
    const targetConnectionSide = dotProduct >= 0 ? "left" : "right";

    const targetWallWidth = connectedWall.wallWidth || getFloorplannerStore().wallWidth;
    const targetEdgePosition = new THREE.Vector2()
      .copy(projectionPoint)
      .addScaledVector(targetPerpendicular, (targetWallWidth / 2) * (targetConnectionSide === "left" ? 1 : -1));

    // Create a pseudo edge connection for outline segmentation
    wallConnections.push({
      sourceWallId: wall.id,
      targetWallId: conn.id,
      sourceEndpoint: sourceEndpoint,
      targetEndpoint: "middle", // Still use "middle" as the endpoint type (could be improved)
      sourceEdgePoint: sourceEdgePointData.edgePosition,
      targetEdgePoint: targetEdgePosition,
      gapDistance: 0,
      connectionAngle: 0,
      id: `${wall.id}-${conn.id}`,
    } as any); // Use 'as any' since we're creating a simplified pseudo connection
  }

  if (wallConnections.length === 0) {
    return [{ start: lineStart.clone(), end: lineEnd.clone() }];
  }

  // Calculate connection points along the line
  let lineDirection = new THREE.Vector2().subVectors(lineEnd, lineStart);
  let lineLength = lineDirection.length();
  if (lineLength === 0) {
    return [{ start: lineStart.clone(), end: lineEnd.clone() }];
  }
  lineDirection.normalize();

  const wallImaginaryLines = calculateWallImaginaryLines(
    wall,
    wall.wallWidth ?? getFloorplannerStore().wallWidth,
  );
  const sourceImaginaryLine = side === "left" ? wallImaginaryLines.left : wallImaginaryLines.right;

  const connectionPoints: Array<{
    position: THREE.Vector2;
    type: "edgeConnection";
    connectedWallId?: string;
    endpoint?: "start" | "end" | "middle";
    intersections: [THREE.Vector2, THREE.Vector2];
  }> = [];

  let startTrimPoint: THREE.Vector2 | undefined;
  let endTrimPoint: THREE.Vector2 | undefined;
  let startTrimScore = Infinity;
  let endTrimScore = Infinity;

  const considerTrimPoint = (
    endpointType: "start" | "end",
    point: THREE.Vector2
  ) => {
    const reference = endpointType === "start" ? lineEnd : lineStart;
    const score = point.distanceToSquared(reference);

    if (endpointType === "start") {
      if (score < startTrimScore) {
        startTrimScore = score;
        startTrimPoint = point.clone();
      }
    } else {
      if (score < endTrimScore) {
        endTrimScore = score;
        endTrimPoint = point.clone();
      }
    }
  };

  for (const conn of wallConnections) {
    // Get the edge point for this wall
    const isSource = conn.sourceWallId === wall.id;
    const endpoint = isSource ? conn.sourceEndpoint : conn.targetEndpoint;
    const otherEndpoint = isSource ? conn.targetEndpoint : conn.sourceEndpoint;

    // CRITICAL FIX: Determine which side this connection is on for THIS wall
    // We need to recalculate the connection side to know if this affects the left or right outline
    const otherWallId = isSource ? conn.targetWallId : conn.sourceWallId;
    const otherWall = getFloorplannerStore().wallsMap.get(otherWallId);

    if (!otherWall) {
      // Skip silently - wall may have been deleted or is from a stale connection reference
      continue;
    }

    // Check if walls are collinear (parallel or anti-parallel)
    // If so, do not trim the outline for this connection.
    // This ensures the outline extends to the endcap, where generatePartialEndCaps will handle the transition.
    if (areWallsCollinear(
      wall,
      otherWall,
      endpoint === "start" || endpoint === "end" ? endpoint : undefined,
      otherEndpoint === "start" || otherEndpoint === "end" ? otherEndpoint : undefined,
    )) {
      // Skip trimming for collinear connections
      continue;
    }

    if (endpoint === "start" || endpoint === "end") {
      const endpointConnectionCount = wallConnections.filter(candidate => {
        const candidateIsSource = candidate.sourceWallId === wall.id;
        const candidateEndpoint = candidateIsSource ? candidate.sourceEndpoint : candidate.targetEndpoint;
        return candidateEndpoint === endpoint;
      }).length;

      if (endpointConnectionCount >= 2) {
        const multiJointTrimPoint = getMultiJointEndpointTrimPoint(
          wall,
          endpoint,
          side,
          lineStart,
          lineEnd,
        );
        if (multiJointTrimPoint) {
          considerTrimPoint(endpoint, multiJointTrimPoint);
          continue;
        }
      }

      const endpointIntersections = getEndpointImaginaryIntersections(
        wall,
        endpoint,
        otherWall,
        getFloorplannerStore().wallWidth,
        {
          forceSharedEndpoint: otherEndpoint === "start" || otherEndpoint === "end",
        }
      );

      if (endpointIntersections) {
        const relevantPoint =
          side === "left" ? endpointIntersections.left : endpointIntersections.right;
        considerTrimPoint(endpoint, relevantPoint);
      }
      continue;
    }

    // Calculate which side of THIS wall the connection is on using position-based detection
    // For edge connections, we check where the OTHER wall extends from this wall's midpoint
    const thisEndpoint = new THREE.Vector2(
      (wall.start.x + wall.end.x) / 2,
      (wall.start.y + wall.end.y) / 2
    );

    const otherWallCenter = new THREE.Vector2(
      (otherWall.start.x + otherWall.end.x) / 2,
      (otherWall.start.y + otherWall.end.y) / 2
    );

    const wallDir = new THREE.Vector2().subVectors(wall.end, wall.start).normalize();
    const perpendicular = new THREE.Vector2(-wallDir.y, wallDir.x);
    const toOtherWall = new THREE.Vector2().subVectors(otherWallCenter, thisEndpoint);

    const dotProduct = toOtherWall.dot(perpendicular);
    const connectionSide =
      Math.abs(dotProduct) < 1e-4 ? "both" : dotProduct >= 0 ? "left" : "right";

    if (connectionSide !== "both" && connectionSide !== side) {
      continue;
    }

    const targetImaginaryLines = calculateWallImaginaryLines(
      otherWall,
      otherWall.wallWidth ?? getFloorplannerStore().wallWidth,
      undefined,
      false,
      otherEndpoint === "start" || otherEndpoint === "end" ? otherEndpoint : undefined,
    );

    const leftIntersection = intersectImaginaryLines(sourceImaginaryLine, targetImaginaryLines.left);
    const rightIntersection = intersectImaginaryLines(sourceImaginaryLine, targetImaginaryLines.right);

    if (!leftIntersection || !rightIntersection) {
      console.log(`        Warning: Unable to find imaginary line intersection for ${otherWallId}`);
      continue;
    }

    const intersectionPoints: [THREE.Vector2, THREE.Vector2] = [
      leftIntersection.point.clone(),
      rightIntersection.point.clone(),
    ];

    const midpoint = intersectionPoints[0].clone().add(intersectionPoints[1]).multiplyScalar(0.5);

    connectionPoints.push({
      position: midpoint,
      type: "edgeConnection",
      connectedWallId: otherWallId,
      endpoint,
      intersections: intersectionPoints,
    });
  }
  if (startTrimPoint) {
    lineStart = startTrimPoint.clone();
  }

  if (endTrimPoint) {
    lineEnd = endTrimPoint.clone();
  }

  lineDirection = new THREE.Vector2().subVectors(lineEnd, lineStart);
  lineLength = lineDirection.length();
  if (lineLength === 0) {
    // If trimming collapsed the outline, fall back to the original span
    return [{ start: originalLineStart.clone(), end: originalLineEnd.clone() }];
  }
  lineDirection.normalize();

  if (connectionPoints.length === 0) {
    // console.log(`    No connection points projected, returning full line`);
    return [{ start: lineStart.clone(), end: lineEnd.clone() }];
  }

  // console.log(`    Found ${connectionPoints.length} connection point(s) after projection`);
  // Sort connection points along the line
  connectionPoints.sort((a, b) => {
    const distA = new THREE.Vector2().subVectors(a.position, lineStart).dot(lineDirection);
    const distB = new THREE.Vector2().subVectors(b.position, lineStart).dot(lineDirection);
    return distA - distB;
  });

  // Create segments between connection points
  const segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];
  let currentPos = lineStart.clone();

  for (let i = 0; i < connectionPoints.length; i++) {
    const intersections = connectionPoints[i].intersections;
    if (!intersections) {
      continue;
    }

    const intersectionPoints = intersections.map(point => ({
      point,
      dist: new THREE.Vector2().subVectors(point, lineStart).dot(lineDirection),
    }));

    intersectionPoints.sort((a, b) => a.dist - b.dist);

    const gapStart = intersectionPoints[0].point;
    const gapEnd = intersectionPoints[1].point;
    const segmentEnd = gapStart.clone();

    // console.log(`        Gap start: (${gapStart.x.toFixed(3)}, ${gapStart.y.toFixed(3)})`);
    // console.log(`        Gap end: (${gapEnd.x.toFixed(3)}, ${gapEnd.y.toFixed(3)})`);
    // console.log(`        Gap size verification: ${gapEnd.distanceTo(gapStart).toFixed(3)} (expected: ${gapSize.toFixed(3)})`);

    // Guard against overshooting for very short segments
    const distToPoint = new THREE.Vector2().subVectors(segmentEnd, currentPos).dot(lineDirection);
    if (distToPoint > 0.0001) {
      segments.push({
        start: currentPos.clone(),
        end: segmentEnd.clone(),
      });
      // console.log(`        Added segment: (${currentPos.x.toFixed(3)}, ${currentPos.y.toFixed(3)}) -> (${segmentEnd.x.toFixed(3)}, ${segmentEnd.y.toFixed(3)})`);
    } else {
      // console.log(`        Skipped segment (too short): distToPoint=${distToPoint.toFixed(6)}`);
    }

    // Resume the next segment at point + halfGap
    currentPos = gapEnd.clone();
  }

  // Add final segment
  segments.push({
    start: currentPos.clone(),
    end: lineEnd.clone(),
  });

  // console.log(`    Created ${segments.length} segment(s) with gaps`);
  // segments.forEach((seg, idx) => {
  //   console.log(`      Segment ${idx + 1}: (${seg.start.x.toFixed(3)}, ${seg.start.y.toFixed(3)}) -> (${seg.end.x.toFixed(3)}, ${seg.end.y.toFixed(3)})`);
  // });

  // Verify bidirectional gap awareness
  // Check if both walls at a connection have the connection in their edgeConnections array
  for (const conn of wallConnections) {
    const otherWallId = conn.sourceWallId === wall.id ? conn.targetWallId : conn.sourceWallId;
    const otherWall = getFloorplannerStore().wallsMap.get(otherWallId);
    if (otherWall) {
      const otherWallHasConnection = (otherWall.edgeConnections || []).some(ec =>
        (ec.sourceWallId === conn.sourceWallId && ec.targetWallId === conn.targetWallId) ||
        (ec.sourceWallId === conn.targetWallId && ec.targetWallId === conn.sourceWallId)
      );
      // console.log(`      Bidirectional check: wall ${otherWallId} ${otherWallHasConnection ? 'has' : 'missing'} this connection`);
    }
  }

  // Safety net: never return an empty outline. If trimming removed everything,
  // fall back to the original span to keep the wall outline visible.
  if (segments.length === 0) {
    return [{
      start: originalLineStart.clone(),
      end: originalLineEnd.clone(),
    }];
  }

  return segments;
}

/**
 * Split a line at mid-point connections with proper side filtering and gap sizing.
 *
 * @param lineStart - Start point of the line
 * @param lineEnd - End point of the line
 * @param wall - The wall this line belongs to
 * @param initialSegments - Initial segments from edge connection splitting
 * @param side - Which side of the wall ("left" or "right")
 * @returns Array of segment start/end pairs
 */
export function splitLineAtMidConnections(
  lineStart: THREE.Vector2,
  lineEnd: THREE.Vector2,
  wall: WallType,
  initialSegments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>,
  side: "left" | "right"
): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> {
  // Get mid-point connections
  const connections = wall.centerConnections || [];
  const midConnections = connections.filter(conn =>
    conn.sourcePosition !== 0 && conn.sourcePosition !== 10000
  );

  if (midConnections.length === 0) {
    return initialSegments;
  }

  // console.log(`    splitLineAtMidConnections (${side}): Found ${midConnections.length} mid-point connection(s)`);

  // Calculate wall direction for side filtering
  const wallDirection = new THREE.Vector2().subVectors(wall.end, wall.start).normalize();

  // Calculate connection points along the line
  const lineDirection = new THREE.Vector2().subVectors(lineEnd, lineStart);
  const lineLength = lineDirection.length();
  if (lineLength === 0) {
    return initialSegments;
  }
  lineDirection.normalize();

  const wallImaginaryLines = calculateWallImaginaryLines(
    wall,
    wall.wallWidth ?? getFloorplannerStore().wallWidth,
  );
  const sourceImaginaryLine = side === "left" ? wallImaginaryLines.left : wallImaginaryLines.right;

  const segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];

  for (const segment of initialSegments) {
    const segmentDirection = new THREE.Vector2().subVectors(segment.end, segment.start);
    const segmentLength = segmentDirection.length();
    if (segmentLength === 0) {
      segments.push(segment);
      continue;
    }
    segmentDirection.normalize();

    let currentPos = segment.start.clone();
    const segmentStart = segment.start.clone();

    // Calculate and sort connection points for this segment
    const connectionPointsWithDist: Array<{
      point: THREE.Vector2;
      dist: number;
      connectedWallId: string;
      connectionSide: "left" | "right";
      targetPosition: number;
    }> = [];

    for (const conn of midConnections) {
      // Get the connected wall to determine side and gap size
      const connectedWall = getFloorplannerStore().wallsMap.get(conn.id);
      if (!connectedWall) {
        // console.log(`      Mid-connection: connected wall ${conn.id} not found`);
        continue;
      }

      // CRITICAL FIX: Determine which side of THIS wall the connection is on
      // For T-junctions, we need to check which side of the blue wall the red wall is actually on
      // We do this by checking where the connected wall's midpoint is relative to this wall's centerline

      // Get connection point on this wall's centerline
      const connectionPoint = getConnectionPoint(wall.start, wall.end, conn.sourcePosition);

      // Get a point on the connected wall (use its midpoint or the opposite end)
      const connectedWallMidpoint = new THREE.Vector2(
        (connectedWall.start.x + connectedWall.end.x) / 2,
        (connectedWall.start.y + connectedWall.end.y) / 2
      );

      // Calculate perpendicular to this wall (left side = counterclockwise 90°)
      const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

      // Vector from connection point to connected wall's midpoint
      const toConnectedWall = new THREE.Vector2()
        .subVectors(connectedWallMidpoint, connectionPoint);

      // Dot product tells us which side: positive = left, negative = right
      const dotProduct = toConnectedWall.dot(perpendicular);
      const connectionSide = dotProduct >= 0 ? "left" : "right";

      // console.log(`      Mid-connection to wall ${conn.id}: on ${connectionSide} side of wall ${wall.id} (dot=${dotProduct.toFixed(3)})`);

      // CRITICAL FIX: Only process this connection if it's on the side we're currently processing
      if (connectionSide !== side) {
        // console.log(`        Skipping - not on ${side} side`);
        continue;
      }

      // CRITICAL FIX: Calculate position using parametric t value, not projection
      // This ensures the gap position is correct relative to the wall centerline
      const wallLength = wall.start.distanceTo(wall.end);
      const t = conn.sourcePosition / wallLength;

      // Calculate position on outline line using the same t value
      const outlinePosition = new THREE.Vector2()
        .copy(lineStart)
        .addScaledVector(new THREE.Vector2().subVectors(lineEnd, lineStart), t);

      // console.log(`        Connection at t=${t.toFixed(3)}, outline position: (${outlinePosition.x.toFixed(3)}, ${outlinePosition.y.toFixed(3)})`);

      // Check if position is within this segment
      const toPosition = new THREE.Vector2().subVectors(outlinePosition, segmentStart);
      const distFromStart = toPosition.dot(segmentDirection);
      const toEnd = new THREE.Vector2().subVectors(segment.end, outlinePosition);
      const distToEnd = toEnd.dot(segmentDirection);

      if (distFromStart >= -0.001 && distToEnd >= -0.001) {
        connectionPointsWithDist.push({
          point: outlinePosition,
          dist: distFromStart,
          connectedWallId: conn.id,
          connectionSide,
          targetPosition: conn.targetPosition,
        });
        // console.log(`        Added to connection points (dist from start: ${distFromStart.toFixed(3)})`);
      } else {
        // console.log(`        Outside segment bounds (distFromStart: ${distFromStart.toFixed(3)}, distToEnd: ${distToEnd.toFixed(3)})`);
      }
    }

    // Sort connection points by distance from segment start
    connectionPointsWithDist.sort((a, b) => a.dist - b.dist);

    // console.log(`      Processing ${connectionPointsWithDist.length} connection(s) for this segment`);

    // Process connections in order
    for (const connPoint of connectionPointsWithDist) {
      const connectedWall = getFloorplannerStore().wallsMap.get(connPoint.connectedWallId);

      if (!connectedWall) {
        // console.log(`        Warning: Connected wall ${connPoint.connectedWallId} not found`);
        continue;
      }

      const targetEndpoint =
        connPoint.targetPosition === WallConnectionStart
          ? "start"
          : connPoint.targetPosition === WallConnectionEnd
            ? "end"
            : undefined;

      const targetImaginaryLines = calculateWallImaginaryLines(
        connectedWall,
        connectedWall.wallWidth ?? getFloorplannerStore().wallWidth,
        undefined,
        false,
        targetEndpoint,
      );

      const leftIntersection = intersectImaginaryLines(sourceImaginaryLine, targetImaginaryLines.left);
      const rightIntersection = intersectImaginaryLines(sourceImaginaryLine, targetImaginaryLines.right);

      if (!leftIntersection || !rightIntersection) {
        // console.log(`        Warning: Unable to find imaginary line intersection for mid-connection`);
        continue;
      }

      const intersectionPoints = [
        leftIntersection.point.clone(),
        rightIntersection.point.clone(),
      ].map(p => ({
        point: p,
        dist: new THREE.Vector2().subVectors(p, segmentStart).dot(segmentDirection),
      }));

      intersectionPoints.sort((a, b) => a.dist - b.dist);

      const gapStart = intersectionPoints[0].point;
      const gapEnd = intersectionPoints[1].point;

      // Add segment up to the gap
      const distToGapStart = new THREE.Vector2().subVectors(gapStart, currentPos).dot(segmentDirection);
      if (distToGapStart > 0.0001) {
        segments.push({
          start: currentPos.clone(),
          end: gapStart.clone(),
        });
        // console.log(`        Added segment before gap: (${currentPos.x.toFixed(3)}, ${currentPos.y.toFixed(3)}) -> (${gapStart.x.toFixed(3)}, ${gapStart.y.toFixed(3)})`);
      }

      // Skip the gap
      currentPos = gapEnd.clone();
    }

    // Add final segment
    if (currentPos.distanceTo(segment.end) > 0.001) {
      segments.push({
        start: currentPos.clone(),
        end: segment.end.clone(),
      });
      // console.log(`        Added final segment: (${currentPos.x.toFixed(3)}, ${currentPos.y.toFixed(3)}) -> (${segment.end.x.toFixed(3)}, ${segment.end.y.toFixed(3)})`);
    }
  }

  return segments;
}

/**
 * Split a line at cutouts (doors, windows, voids).
 *
 * @param wallDirection - Direction of the wall
 * @param lineStart - Start point of the line
 * @param lineEnd - End point of the line
 * @param wall - The wall this line belongs to
 * @param side - Which side of the wall ("left" or "right")
 * @param initialSegments - Initial segments from previous splitting
 * @returns Array of segment start/end pairs
 */
export function splitLineAtCutouts(
  wallDirection: THREE.Vector2,
  lineStart: THREE.Vector2,
  lineEnd: THREE.Vector2,
  wall: WallType,
  side: "left" | "right",
  initialSegments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>
): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> {
  // Get cutouts
  const cutouts = wall.cutouts || [];

  if (cutouts.length === 0) {
    return initialSegments;
  }

  const segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];

  for (const segment of initialSegments) {
    const segmentDirection = new THREE.Vector2().subVectors(segment.end, segment.start);
    const segmentLength = segmentDirection.length();
    if (segmentLength === 0) {
      segments.push(segment);
      continue;
    }
    segmentDirection.normalize();

    let currentPos = segment.start.clone();
    const segmentStart = segment.start.clone();

    // Process cutouts that apply to this side
    for (const cutout of cutouts) {
      // Check if cutout applies to this side
      if (!cutout.appliesTo.has(side) && !cutout.appliesTo.has("all")) {
        continue;
      }

      // Calculate cutout start and end along the wall
      const cutoutStartT = cutout.positionFromStart / 10000;
      const cutoutEndT = (cutout.positionFromStart + cutout.length) / 10000;

      const cutoutStartPoint = new THREE.Vector2()
        .copy(wall.start)
        .addScaledVector(new THREE.Vector2().subVectors(wall.end, wall.start), cutoutStartT);
      const cutoutEndPoint = new THREE.Vector2()
        .copy(wall.start)
        .addScaledVector(new THREE.Vector2().subVectors(wall.end, wall.start), cutoutEndT);

      // Project cutout boundaries onto the line
      const cutoutStartProjected = projectPointOnLine(cutoutStartPoint, segmentStart, segment.end);
      const cutoutEndProjected = projectPointOnLine(cutoutEndPoint, segmentStart, segment.end);

      if (cutoutStartProjected && cutoutEndProjected) {
        // Add segment up to the cutout
        segments.push({
          start: currentPos.clone(),
          end: cutoutStartProjected.clone(),
        });

        // Skip the cutout
        currentPos = cutoutEndProjected.clone();
      }
    }

    // Add final segment
    if (currentPos.distanceTo(segment.end) > 0.001) {
      segments.push({
        start: currentPos.clone(),
        end: segment.end.clone(),
      });
    }
  }

  return segments;
}

/**
 * Split a line at symbol attachment positions (doors, windows, voids).
 * This creates gaps in the outline where symbols are attached to the wall.
 * 
 * Unlike the cutouts system (which affects fill shapes), this only affects outline rendering.
 *
 * @param wall - The wall to process
 * @param side - Which side of the wall ("left" or "right")
 * @param initialSegments - Initial segments from previous splitting
 * @returns Array of segment start/end pairs with gaps at symbol positions
 */
export function splitLineAtSymbolAttachments(
  wall: WallType,
  side: "left" | "right",
  initialSegments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>
): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> {
  const symbolAttachments = wall.symbolAttachments || [];

  if (symbolAttachments.length === 0) {
    return initialSegments;
  }

  // Build a list of gaps based on symbol attachments
  interface SymbolGap {
    centerPosition: number;  // World units from wall start
    halfLength: number;      // Half the symbol length in world units
  }

  const gaps: SymbolGap[] = [];

  for (const attachment of symbolAttachments) {
    const symbol = getFloorplannerStore().symbolsMap.get(attachment.symbolId);
    if (!symbol) continue;

    // Get the symbol length based on type
    // Note: Doors and double doors include frame width on both sides and have an offset
    // because the door position is at one edge, not the center
    let symbolTotalWidth = 0;
    let centerOffset = 0; // Offset from positionFromStart to the center of the gap

    if (isSlidingDoorType(symbol)) {
      const doorWidth = (symbol as any).doorWidth;
      const frameWidth = (symbol as any).doorFrameWidth || getFloorplannerStore().doorFrameWidth;
      // Leaf + clear passage beside it (2 × doorWidth) plus frames.
      symbolTotalWidth = doorWidth * 2 + frameWidth * 2;
      centerOffset = doorWidth;
    } else if (isDoorType(symbol)) {
      const doorWidth = (symbol as any).doorWidth;
      const frameWidth = (symbol as any).doorFrameWidth || getFloorplannerStore().doorFrameWidth;
      // Total width includes frame on both sides
      symbolTotalWidth = doorWidth + frameWidth * 2;
      // Door position is at the left edge (hinge), not center
      // The gap should be centered on the door blade + frames
      centerOffset = doorWidth / 2;
    } else if (isDoubleDoorType(symbol)) {
      const doubleDoorWidth = (symbol as any).doubleDoorWidth; // Width of ONE blade
      const frameWidth = (symbol as any).doorFrameWidth || getFloorplannerStore().doorFrameWidth;
      // Total width includes both door blades (2x) plus frame on both sides
      symbolTotalWidth = doubleDoorWidth * 2 + frameWidth * 2;
      // Double door position is at the left edge, center is at doubleDoorWidth from position
      centerOffset = doubleDoorWidth;
    } else if (isWindowType(symbol)) {
      symbolTotalWidth = (symbol as any).windowLength;
      centerOffset = 0;
    } else if (isVoidType(symbol)) {
      symbolTotalWidth = (symbol as any).voidLength;
      centerOffset = 0;
    }

    if (symbolTotalWidth > 0) {
      gaps.push({
        centerPosition: attachment.positionFromStart + centerOffset,
        halfLength: symbolTotalWidth / 2,
      });
    }
  }

  if (gaps.length === 0) {
    return initialSegments;
  }

  // Sort gaps by position
  gaps.sort((a, b) => a.centerPosition - b.centerPosition);

  const wallDirection = new THREE.Vector2().subVectors(wall.end, wall.start).normalize();

  const resultSegments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];

  for (const segment of initialSegments) {
    const segmentLength = segment.start.distanceTo(segment.end);
    if (segmentLength < 0.001) {
      resultSegments.push(segment);
      continue;
    }

    // Project segment endpoints onto the wall centerline to find their positions
    const toSegStart = new THREE.Vector2().subVectors(segment.start, wall.start);
    const toSegEnd = new THREE.Vector2().subVectors(segment.end, wall.start);
    const segStartT = toSegStart.dot(wallDirection);
    const segEndT = toSegEnd.dot(wallDirection);

    // Ensure segment goes from lower T to higher T
    const minT = Math.min(segStartT, segEndT);
    const maxT = Math.max(segStartT, segEndT);

    let currentT = minT;

    for (const gap of gaps) {
      const gapStart = gap.centerPosition - gap.halfLength;
      const gapEnd = gap.centerPosition + gap.halfLength;

      // Skip gaps that are entirely outside this segment
      if (gapEnd <= minT || gapStart >= maxT) {
        continue;
      }

      // Clamp gap to segment bounds
      const clampedGapStart = Math.max(gapStart, minT);
      const clampedGapEnd = Math.min(gapEnd, maxT);

      // Add segment from currentT to gap start (if there's space)
      if (clampedGapStart > currentT + 0.001) {
        // Calculate the actual points on the outline (not centerline)
        // We need to find where the outline intersects these T values
        const tRatio1 = (currentT - minT) / (maxT - minT);
        const tRatio2 = (clampedGapStart - minT) / (maxT - minT);

        const point1 = new THREE.Vector2().lerpVectors(segment.start, segment.end, tRatio1);
        const point2 = new THREE.Vector2().lerpVectors(segment.start, segment.end, tRatio2);

        resultSegments.push({
          start: point1,
          end: point2,
        });
      }

      // Skip past the gap
      currentT = clampedGapEnd;
    }

    // Add final segment from currentT to segment end
    if (maxT > currentT + 0.001) {
      const tRatio = (currentT - minT) / (maxT - minT);
      const point1 = new THREE.Vector2().lerpVectors(segment.start, segment.end, Math.max(0, tRatio));

      resultSegments.push({
        start: point1,
        end: segment.end.clone(),
      });
    }
  }

  return resultSegments;
}

/**
 * Project a point onto a line segment.
 *
 * @param point - The point to project
 * @param lineStart - Start of the line segment
 * @param lineEnd - End of the line segment
 * @returns Projected point or null if outside bounds
 */
export function projectPointOnLine(
  point: THREE.Vector2,
  lineStart: THREE.Vector2,
  lineEnd: THREE.Vector2
): THREE.Vector2 | null {
  const line = new THREE.Vector2().subVectors(lineEnd, lineStart);
  const lineLength = line.length();

  if (lineLength === 0) {
    return null;
  }

  const toPoint = new THREE.Vector2().subVectors(point, lineStart);
  const projection = toPoint.dot(line) / lineLength;

  if (projection < 0 || projection > lineLength) {
    return null;
  }

  return new THREE.Vector2().copy(lineStart).addScaledVector(line.normalize(), projection);
}

/**
 * Calculate appropriate gap size based on context.
 *
 * @param wall - The wall
 * @param connectionType - Type of connection
 * @returns Gap size in world units
 */
export function calculateGapSize(wall: WallType, connectionType: "edge" | "mid" | "cutout"): number {
  switch (connectionType) {
    case "edge":
      // Small fixed gap for edge connections
      return 0.02;
    case "mid": {
      // Use connected wall width / 4 for mid connections
      // CLARIFICATION: WallConnectionType.id maps to the connected wall's ID.
      // We look up the connected wall from getFloorplannerStore().wallsMap to get its width.
      // If lookup fails (e.g., wall not found or no mid-connections), we fall back to
      // the current wall's width / 4 to ensure a reasonable gap size.
      const connections = wall.centerConnections || [];
      if (connections.length > 0) {
        // Get the first mid-connection (not at endpoints: 0 or 10000)
        const midConnection = connections.find(conn =>
          conn.sourcePosition !== WallConnectionStart && conn.sourcePosition !== WallConnectionEnd
        );
        if (midConnection) {
          // Attempt to get the connected wall using WallConnectionType.id
          const connectedWall = getFloorplannerStore().wallsMap.get(midConnection.id);
          if (connectedWall) {
            // Use the connected wall's width / 4 for the gap
            return (connectedWall.wallWidth || getFloorplannerStore().wallWidth) / 4;
          }
        }
      }
      // Fallback to current wall width / 4 if no connection found or lookup failed
      return (wall.wallWidth || getFloorplannerStore().wallWidth) / 4;
    }
    case "cutout":
      // No additional gap - cutout defines the gap
      return 0;
    default:
      return 0.02;
  }
}

/**
 * Merge segments that are very close together.
 *
 * @param segments - Array of segments
 * @param tolerance - Minimum gap to keep separate (default 0.001)
 * @param lineStart - Start point of the original line (for parametric distance calculation)
 * @param lineDirection - Normalized direction vector of the original line
 * @returns Array of merged segments
 */
export function mergeOverlappingSegments(
  segments: Array<{ start: THREE.Vector2; end: THREE.Vector2 }>,
  tolerance: number = 0.001,
  lineStart?: THREE.Vector2,
  lineDirection?: THREE.Vector2
): Array<{ start: THREE.Vector2; end: THREE.Vector2 }> {
  if (segments.length === 0) {
    return [];
  }

  // Sort segments by parametric distance along the original line
  const sortedSegments = [...segments].sort((a, b) => {
    if (lineStart && lineDirection) {
      // Sort by distance from lineStart along the line direction
      const distA = new THREE.Vector2().subVectors(a.start, lineStart).dot(lineDirection);
      const distB = new THREE.Vector2().subVectors(b.start, lineStart).dot(lineDirection);
      return distA - distB;
    } else {
      // Fallback to absolute distance from origin if line parameters not provided
      const distA = a.start.length();
      const distB = b.start.length();
      return distA - distB;
    }
  });

  const merged: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];
  let current = { start: sortedSegments[0].start.clone(), end: sortedSegments[0].end.clone() };

  for (let i = 1; i < sortedSegments.length; i++) {
    const segment = sortedSegments[i];
    const gap = new THREE.Vector2().subVectors(segment.start, current.end).length();

    if (gap < tolerance) {
      // Merge segments
      console.log(`      Merging segments: gap=${gap.toFixed(6)} < tolerance=${tolerance.toFixed(6)}`);
      current.end.copy(segment.end);
    } else {
      // Add current segment and start new one
      if (gap < 0.02) {
        console.log(`      Keeping gap: gap=${gap.toFixed(6)} (should be 0.02 for edge connections)`);
      }
      merged.push(current);
      current = { start: segment.start.clone(), end: segment.end.clone() };
    }
  }

  merged.push(current);

  // Filter out zero-length or near-zero segments
  const epsilon = 1e-4;
  const filtered = merged.filter(segment => {
    const length = segment.start.distanceTo(segment.end);
    return length >= epsilon;
  });

  return filtered;
}

/**
 * Project a point onto a line and return the parameter t.
 * The line is defined as: p1 + t * capDir (where capDir is normalized)
 * @param p - Point to project
 * @param p1 - Start point of the line
 * @param capDir - Normalized direction vector of the line
 * @returns Parameter t representing distance along the line from p1
 */
function projectToT(p: THREE.Vector2, p1: THREE.Vector2, capDir: THREE.Vector2): number {
  const toPoint = new THREE.Vector2().subVectors(p, p1);
  return toPoint.dot(capDir);
}

/**
 * Generate partial endcap segments for edge-connected walls with mismatched widths.
 * This fills the "gap" in the outline where a wider wall connects to a narrower one.
 */
function generatePartialEndCaps(
  wall: WallType,
  endpoint: 'start' | 'end',
  p1: THREE.Vector2, // Left corner (start of cap)
  p2: THREE.Vector2, // Right corner (end of cap)
  thickness: number,
  color: number
): OutlineSegment[] {
  const segments: OutlineSegment[] = [];

  // Line segment direction (p1 to p2)
  const capDir = new THREE.Vector2().subVectors(p2, p1);
  const capLength = capDir.length();
  if (capLength < 0.001) {
    return [];
  }
  capDir.normalize();


  // We represent the endcap as interval [0, capLength]
  // We want to subtract intervals covered by connected walls
  // Store source points to generate connector lines bridging the gap
  type CoveredInterval = {
    start: number;
    end: number;
    startPoint: THREE.Vector2 | null; // Corner of connected wall corresponding to 'start'
    endPoint: THREE.Vector2 | null;   // Corner of connected wall corresponding to 'end'
    allowConnectors: boolean;
  };
  const coveredIntervals: CoveredInterval[] = [];

  // Helper to intersect outline with endcap line and update min/max
  // Uses intersection logic to find EXACT cut point, ensuring visual continuity
  const projectAndUpdate = (
    p: THREE.Vector2,
    otherWallDir: THREE.Vector2,
    isConnectionCorner: boolean,
    current: { min: number, max: number, minP: THREE.Vector2 | null, maxP: THREE.Vector2 | null }
  ) => {
    // Calculate the t value (position along endcap line) for coverage calculation
    const t = getIntersectionT(p1, capDir, p, otherWallDir);

    if (t < current.min) {
      current.min = t;
      // Store the ACTUAL point coordinates, not the projected point on the endcap line!
      current.minP = isConnectionCorner ? p.clone() : null;
    }
    if (t > current.max) {
      current.max = t;
      // Store the ACTUAL point coordinates, not the projected point on the endcap line!
      current.maxP = isConnectionCorner ? p.clone() : null;
    }
  };

  // Find all connected walls at this endpoint
  // 1. Edge Connections
  const endpointEdgeConnections = getEndpointEdgeConnections(wall, endpoint);
  for (const conn of endpointEdgeConnections) {
    const isSource = conn.sourceWallId === wall.id && conn.sourceEndpoint === endpoint;
    const isTarget = conn.targetWallId === wall.id && conn.targetEndpoint === endpoint;

    if (isSource || isTarget) {
      const otherWallId = isSource ? conn.targetWallId : conn.sourceWallId;
      const otherWall = getFloorplannerStore().wallsMap.get(otherWallId);
      if (otherWall) {
        const otherEndpoint = isSource ? conn.targetEndpoint : conn.sourceEndpoint;

        // Check if walls are collinear
        if (!areWallsCollinear(
          wall,
          otherWall,
          endpoint,
          otherEndpoint === "start" || otherEndpoint === "end" ? otherEndpoint : undefined,
        )) {
          // If not collinear (e.g. 90 degree corner), suppress endcap entirely
          coveredIntervals.push({ start: 0, end: capLength, startPoint: null, endPoint: null, allowConnectors: false });
          continue;
        }

        const otherWallDir = getWallDirectionAtConnection(
          otherWall,
          otherEndpoint === "start" || otherEndpoint === "end" ? otherEndpoint : undefined,
        );

        const bounds = { min: Infinity, max: -Infinity, minP: null as THREE.Vector2 | null, maxP: null as THREE.Vector2 | null };

        if (otherEndpoint === "start" || otherEndpoint === "end") {
          // End-to-End connection
          // For COLLINEAR walls, getEndpointImaginaryIntersections returns the SOURCE wall's
          // corners (not useful for coverage). We need to use the OTHER wall's outline edges
          // to correctly determine which portion of this wall's endcap is covered.
          // 
          // For NON-COLLINEAR walls, we use intersection logic to find where this wall's
          // outline edges intersect with the other wall's outline edges.

          if (areWallsCollinear(wall, otherWall, endpoint, otherEndpoint)) {
            // COLLINEAR CASE: Use direct outline edge calculation from OTHER wall
            // This correctly determines coverage based on the other wall's width
            const otherConnectionPoint = toVector2(
              otherEndpoint === 'start' ? otherWall.start : otherWall.end
            );
            const otherLineWeight = getFloorplannerStore().convertLineWeightToWorld(otherWall.lineWeight || getFloorplannerStore().wallLineWeight);
            const otherWallWidth = otherWall.wallWidth || getFloorplannerStore().wallWidth;
            const otherPerp = new THREE.Vector2(-otherWallDir.y, otherWallDir.x);
            const offset = (otherWallWidth / 2) + (otherLineWeight / 2);

            // Calculate OUTLINE edges (not fill edges) of the other wall at its connection point
            const otherLeftOutline = otherConnectionPoint.clone()
              .add(otherPerp.clone().multiplyScalar(offset));
            const otherRightOutline = otherConnectionPoint.clone()
              .sub(otherPerp.clone().multiplyScalar(offset));

            // Use these outline points directly as the connection corners
            projectAndUpdate(otherLeftOutline, otherWallDir, true, bounds);
            projectAndUpdate(otherRightOutline, otherWallDir, true, bounds);
          } else {
            // NON-COLLINEAR CASE: Use intersection logic
            const intersections = getEndpointImaginaryIntersections(
              wall,
              endpoint,
              otherWall,
              wall.wallWidth || getFloorplannerStore().wallWidth,
              {
                forceSharedEndpoint: true,
                includeLineWeight: true,
                preferClosestPerSourceSide: true,
              }
            );

            if (intersections) {
              // Use the intersection points directly - these are at the OUTLINE edges
              projectAndUpdate(intersections.left, otherWallDir, true, bounds);
              projectAndUpdate(intersections.right, otherWallDir, true, bounds);
            }
          }

          // Far end projection removed - strictly use local connection geometry

        } else {
          // T-junction
          const thisEndpointPos = endpoint === 'start' ? wall.start : wall.end;

          // Calculate OUTLINE edges (not fill edges) at the T-junction point
          const otherLineWeight = getFloorplannerStore().convertLineWeightToWorld(otherWall.lineWeight || getFloorplannerStore().wallLineWeight);
          const otherWallWidth = otherWall.wallWidth || getFloorplannerStore().wallWidth;
          const otherPerp = new THREE.Vector2(-otherWallDir.y, otherWallDir.x);

          const otherLeftOutline = thisEndpointPos.clone()
            .add(otherPerp.clone().multiplyScalar((otherWallWidth / 2) + (otherLineWeight / 2)));
          const otherRightOutline = thisEndpointPos.clone()
            .sub(otherPerp.clone().multiplyScalar((otherWallWidth / 2) + (otherLineWeight / 2)));

          projectAndUpdate(otherLeftOutline, otherWallDir, true, bounds);
          projectAndUpdate(otherRightOutline, otherWallDir, true, bounds);

          // Far ends projection removed - strictly use local connection geometry
        }

        if (bounds.min !== Infinity) {
          const isLocallyCollinear =
            otherEndpoint === "start" || otherEndpoint === "end"
              ? areWallsCollinear(wall, otherWall, endpoint, otherEndpoint)
              : areWallsCollinear(wall, otherWall, endpoint);
          coveredIntervals.push({
            start: bounds.min,
            end: bounds.max,
            startPoint: bounds.minP,
            endPoint: bounds.maxP,
            allowConnectors: !isLocallyCollinear,
          });
        }
      }
    }
  }

  // 2. Center Connections (Legacy T-Junctions check)
  // Only process if NO edge connections were found (to avoid duplicates)
  const hadEdgeConnections = endpointEdgeConnections.length > 0;

  if (!hadEdgeConnections) {
    const centerConnections = wall.centerConnections || [];
    const endpointPosition = endpoint === 'start' ? WallConnectionStart : WallConnectionEnd;

    for (const conn of centerConnections) {
      if (conn.sourcePosition === endpointPosition) {
        const otherWall = getFloorplannerStore().wallsMap.get(conn.id);
        if (otherWall) {
          if (!areWallsCollinear(wall, otherWall)) {
            coveredIntervals.push({ start: 0, end: capLength, startPoint: null, endPoint: null, allowConnectors: false });
            continue;
          }

          const otherWallDir = new THREE.Vector2().subVectors(otherWall.end, otherWall.start).normalize();
          const bounds = { min: Infinity, max: -Infinity, minP: null as THREE.Vector2 | null, maxP: null as THREE.Vector2 | null };
          const thisEndpointPos = endpoint === 'start' ? wall.start : wall.end;

          // Calculate OUTLINE edges (not fill edges) at the T-junction point
          const otherLineWeight = getFloorplannerStore().convertLineWeightToWorld(otherWall.lineWeight || getFloorplannerStore().wallLineWeight);
          const otherWallWidth = otherWall.wallWidth || getFloorplannerStore().wallWidth;
          const otherPerp = new THREE.Vector2(-otherWallDir.y, otherWallDir.x);

          const otherLeftOutline = thisEndpointPos.clone()
            .add(otherPerp.clone().multiplyScalar((otherWallWidth / 2) + (otherLineWeight / 2)));
          const otherRightOutline = thisEndpointPos.clone()
            .sub(otherPerp.clone().multiplyScalar((otherWallWidth / 2) + (otherLineWeight / 2)));

          projectAndUpdate(otherLeftOutline, otherWallDir, true, bounds);
          projectAndUpdate(otherRightOutline, otherWallDir, true, bounds);

          // Far ends projection removed - strictly use local connection geometry

          if (bounds.min !== Infinity) {
            coveredIntervals.push({
              start: bounds.min,
              end: bounds.max,
              startPoint: bounds.minP,
              endPoint: bounds.maxP,
              allowConnectors: !areWallsCollinear(wall, otherWall, endpoint),
            });
          }
        }
      }
    }
  }

  // Merge intervals
  coveredIntervals.sort((a, b) => a.start - b.start);
  const merged: CoveredInterval[] = [];
  if (coveredIntervals.length > 0) {
    let curr = coveredIntervals[0];
    for (let i = 1; i < coveredIntervals.length; i++) {
      const next = coveredIntervals[i];
      if (next.start < curr.end) {
        // Overlap - Merge
        const newStart = curr.start;
        const newEnd = Math.max(curr.end, next.end);
        const newStartPoint = curr.startPoint;
        const newEndPoint = next.end > curr.end ? next.endPoint : curr.endPoint;
        curr = {
          start: newStart,
          end: newEnd,
          startPoint: newStartPoint,
          endPoint: newEndPoint,
          allowConnectors: curr.allowConnectors || next.allowConnectors,
        };
      } else {
        merged.push(curr);
        curr = next;
      }
    }
    merged.push(curr);
  }

  // Generate free segments and connectors
  let t = 0;
  const epsilon = 0.01; // Tolerance

  for (const interval of merged) {
    // If there is a gap before this interval, draw partial endcap
    if (interval.start > t + epsilon) {
      const s = new THREE.Vector2().copy(p1).addScaledVector(capDir, t);
      const e = new THREE.Vector2().copy(p1).addScaledVector(capDir, interval.start);
      segments.push({
        id: `${wall.id}-endcap-${endpoint}-${t.toFixed(2)}`,
        wallId: wall.id,
        side: 'left',
        start: s,
        end: e,
        thickness,
        color,
        dashed: false
      });

      // Connector 1: From end of partial cap (e) to interval start point (corner of other wall)
      if (interval.allowConnectors && interval.startPoint) {
        segments.push({
          id: `${wall.id}-connector-${endpoint}-start-${interval.start.toFixed(2)}`,
          wallId: wall.id,
          side: 'left',
          start: e.clone(),
          end: interval.startPoint.clone(),
          thickness,
          color,
          dashed: false
        });
      }
    } else if (interval.start > t - epsilon) {
      // Collinear case (no gap in t), but potentially Z-gap to other wall
      if (interval.allowConnectors && interval.startPoint) {
        const pAtStart = new THREE.Vector2().copy(p1).addScaledVector(capDir, interval.start);
        if (pAtStart.distanceTo(interval.startPoint) > 0.001) {
          segments.push({
            id: `${wall.id}-connector-${endpoint}-start-${interval.start.toFixed(2)}`,
            wallId: wall.id,
            side: 'left',
            start: pAtStart,
            end: interval.startPoint.clone(),
            thickness,
            color,
            dashed: false
          });
        }
      }
    }

    t = Math.max(t, interval.end);

    // Connector 2: From interval end point
    if (interval.allowConnectors && interval.endPoint) {
      const pAtEnd = new THREE.Vector2().copy(p1).addScaledVector(capDir, interval.end);
      if (pAtEnd.distanceTo(interval.endPoint) > 0.001) {
        segments.push({
          id: `${wall.id}-connector-${endpoint}-end-${interval.end.toFixed(2)}`,
          wallId: wall.id,
          side: 'left',
          start: interval.endPoint.clone(), // Draw FROM outline TO cap
          end: pAtEnd,
          thickness,
          color,
          dashed: false
        });
      }
    }
  }

  // Check for gap after last interval
  if (t < capLength - epsilon) {
    const s = new THREE.Vector2().copy(p1).addScaledVector(capDir, t);
    const e = new THREE.Vector2().copy(p1).addScaledVector(capDir, capLength);
    segments.push({
      id: `${wall.id}-endcap-${endpoint}-${t.toFixed(2)}`,
      wallId: wall.id,
      side: 'left',
      start: s,
      end: e,
      thickness,
      color,
      dashed: false
    });
  }

  return segments;
}

/**
 * Helper: Calculate intersection t of a line (origin, dir) with another line passing through
 * otherPoint with direction otherDir.
 * Returns t such that Intersection = origin + t * dir.
 * If lines are parallel, falls back to orthogonal projection.
 */
function getIntersectionT(
  origin: THREE.Vector2,
  dir: THREE.Vector2, // capDir
  otherPoint: THREE.Vector2,
  otherDir: THREE.Vector2 // wallVec
): number {
  const det = dir.x * otherDir.y - dir.y * otherDir.x;
  if (Math.abs(det) < 1e-5) {
    // Parallel - fallback to orthogonal projection
    const v = new THREE.Vector2().subVectors(otherPoint, origin);
    return v.dot(dir);
  }
  const dx = otherPoint.x - origin.x;
  const dy = otherPoint.y - origin.y;
  // Intersection of: origin + t*dir = otherPoint + u*otherDir
  // t*dir.x - u*otherDir.x = dx
  // t*dir.y - u*otherDir.y = dy
  // Solve for t using Cramer's rule
  // t = (dx * -otherDir.y - dy * -otherDir.x) / det
  // t = (-dx * otherDir.y + dy * otherDir.x) / det
  // Wait, system is:
  // t * dir.x + u * (-otherDir.x) = dx
  // t * dir.y + u * (-otherDir.y) = dy
  // det = dir.x * (-otherDir.y) - dir.y * (-otherDir.x) = - (dir x otherDir)
  // t_num = dx * (-otherDir.y) - dy * (-otherDir.x) = - (dx * otherDir.y - dy * otherDir.x)
  // t = t_num / det = (dx * otherDir.y - dy * otherDir.x) / (dir.x * otherDir.y - dir.y * otherDir.x)
  // This matches the formula in the comment.
  return (dx * otherDir.y - dy * otherDir.x) / (dir.x * otherDir.y - dir.y * otherDir.x);
}
